// src/pages/shop/ShopCashier.jsx
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { shop as shopApi, products as productsApi, sales as salesApi, payments as paymentsApi, debtors as debtorsApi, categories as categoriesApi } from '../../api';
import { QRCodeSVG } from 'qrcode.react';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import { formatMoney, getCurrencySymbol } from '../../data/currencies';
import { useMyCurrency, useMyShop } from '../../hooks/useMyShop';
import { useAuth } from '../../hooks/useAuth';
import { isComposite, computeAvailable, getMainPhoto } from '../../utils/composite';
import NumPad, { parseQty } from '../../components/NumPad';
import { SHOP_NAV, useShopNav } from './ShopHome';
import { useToast } from '../../hooks/useToast';
import { useReloadOnFocus } from '../../hooks/useReloadOnFocus';
import { isModuleOn, canSeeModule } from '../../config/modules';
import { isGeneratedBarcode, NONAME_BARCODE } from '../../utils/generateBarcode';
import { errorMessage } from '../../utils/errorMessage';
import { isValidWhatsapp } from '../../utils/whatsapp';

import { shareQR, saveQR, hasNativeShare } from '../../utils/qrShare';
import ScanButton from '../../components/ScanButton';
import { payQrWithAmount } from '../../utils/elqr';
import { setImmersive, isNative } from '../../utils/immersive';

// Расчёт скидки в валюте: процент или сумма
function calcDiscountAmount(baseAmount, type, value) {
  const v = Number(value) || 0;
  if (!type || v <= 0 || baseAmount <= 0) return 0;
  let amount = 0;
  if (type === 'percent') amount = (baseAmount * Math.min(v, 100)) / 100;
  else if (type === 'amount') amount = v;
  amount = Math.min(amount, baseAmount);
  amount = Math.max(amount, 0);
  return Math.round(amount * 100) / 100;
}

function unitLabel(u) {
  if (u === 'kg' || u === 'кг') return 'кг';
  if (u === 'l' || u === 'л')   return 'л';
  return 'шт';
}

export default function ShopCashier() {
  const { t } = useTranslation();
  const { show, ToastView } = useToast();
  const navItems = useShopNav();
  const currency = useMyCurrency();
  const myShop = useMyShop();
  const { user, isOwner, can } = useAuth();
  const hasDiscountPerm = isOwner || can('discounts');
  const discountsEnabled = myShop?.discountsEnabled !== false;
  // Лимиты сотрудника. Владелец делает любые скидки.
  const maxDiscPercent = isOwner ? 100 : (Number(user?.permissions?.maxDiscountPercent) || 0);
  const maxDiscAmount  = isOwner ? Number.POSITIVE_INFINITY : (Number(user?.permissions?.maxDiscountAmount) || 0);
  // Хотя бы один тип скидки доступен
  const canDiscount = hasDiscountPerm && (isOwner || maxDiscPercent > 0 || maxDiscAmount > 0);

  const [shop, setShop]           = useState(null);
  const [products, setProducts]   = useState([]);
  const [cats, setCats]           = useState([]);
  // Режим «только несканируемые» (галочка «НС» на кассе, помним на устройстве)
  const [nsOnly, setNsOnly] = useState(() => {
    try { return localStorage.getItem('cashierNsOnly') === '1'; } catch (e) { return false; }
  });
  // Навигация по категориям (для многоуровневой кассы): null | rootCatId | subCatId
  const [catPath, setCatPath] = useState([]); // стек id категорий
  const [cart, setCart]           = useState([]);
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [processing, setProcessing] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [qrResult, setQrResult]   = useState(null);

  // Ожидание оплаты баллами:
  // null — нет ожидания
  // { token, amount, expiresAt } — показываем QR, ждём оплату клиентом
  const [pendingPayment, setPendingPayment] = useState(null);
  const pollRef = useRef(null);

  const [activeProduct, setActiveProduct] = useState(null);
  const [qtyInput, setQtyInput] = useState('');
  const [priceInput, setPriceInput] = useState(''); // переопределённая цена позиции
  const [activeField, setActiveField] = useState('qty'); // 'qty' | 'price'
  const [editingItem, setEditingItem] = useState(null);

  // Скидки
  // discountTarget: null | 'sale' | { id: cartItemId } — какой объект редактируем в DiscountSheet
  const [discountTarget, setDiscountTarget] = useState(null);
  // Скидка на чек целиком: { type: 'percent'|'amount', value: number } | null
  const [saleDiscount, setSaleDiscount] = useState(null);
  const qtyInputRef = useRef(null);

  // Refs на QR-элементы для шаринга/сохранения картинки
  const receiptQrRef = useRef(null);   // QR-чек после продажи
  const paymentQrRef = useRef(null);   // QR-оплаты баллами
  const [sharing, setSharing] = useState(false);
  const nativeShare = hasNativeShare();

  useEffect(() => { load(); }, []);

  // ───────────────────────────────────────────────────────────
  // QR: шаринг и сохранение
  // ───────────────────────────────────────────────────────────

  const buildReceiptShareText = () => {
    const shopName = shop?.name || 'Alsatuu';
    const points   = qrResult?.pointsEarned || 0;
    return t('cashier.shareReceiptText', { shop: shopName, points });
  };

  const buildPaymentShareText = () => {
    const shopName = shop?.name || 'Alsatuu';
    const amount   = formatMoney(pendingPayment?.amount || 0, currency);
    return t('cashier.sharePaymentText', { shop: shopName, amount });
  };

  const shareReceiptQR = async () => {
    if (!receiptQrRef.current || sharing) return;
    setSharing(true);
    try {
      const svgEl = receiptQrRef.current.querySelector('svg');
      const result = await shareQR({
        svgEl,
        filename: `alsatuu-receipt-${qrResult?.token?.slice(0, 6) || 'qr'}.png`,
        title: 'Alsatuu',
        text: buildReceiptShareText(),
      });
      if (result.method === 'download-fallback') {
        show(t('cashier.qrSavedToDownloads'), 'success');
      }
    } catch (e) {
      show(t('cashier.shareError'), 'error');
    }
    setSharing(false);
  };

  const savePaymentQR = async () => {
    if (!paymentQrRef.current || sharing) return;
    setSharing(true);
    try {
      const svgEl = paymentQrRef.current.querySelector('svg');
      await saveQR({
        svgEl,
        filename: `alsatuu-payment-${(pendingPayment?.token || 'qr').slice(0, 8)}.png`,
      });
      show(t('cashier.qrSavedSuccess'), 'success');
    } catch (e) {
      show(t('cashier.shareError'), 'error');
    }
    setSharing(false);
  };

  const saveReceiptQR = async () => {
    if (!receiptQrRef.current || sharing) return;
    setSharing(true);
    try {
      const svgEl = receiptQrRef.current.querySelector('svg');
      await saveQR({
        svgEl,
        filename: `alsatuu-receipt-${qrResult?.token?.slice(0, 6) || 'qr'}.png`,
      });
      show(t('cashier.qrSavedSuccess'), 'success');
    } catch (e) {
      show(t('cashier.shareError'), 'error');
    }
    setSharing(false);
  };

  const sharePaymentQR = async () => {
    if (!paymentQrRef.current || sharing) return;
    setSharing(true);
    try {
      const svgEl = paymentQrRef.current.querySelector('svg');
      const result = await shareQR({
        svgEl,
        filename: `alsatuu-payment-${(pendingPayment?.token || 'qr').slice(0, 8)}.png`,
        title: 'Alsatuu',
        text: buildPaymentShareText(),
      });
      if (result.method === 'download-fallback') {
        show(t('cashier.qrSavedToDownloads'), 'success');
      }
    } catch (e) {
      show(t('cashier.shareError'), 'error');
    }
    setSharing(false);
  };

  const load = async () => {
    // Есть прошлый список — показываем кассу сразу, свежий подтянется ниже
    const cachedProducts = productsApi.cached();
    if (cachedProducts) {
      setProducts(cachedProducts);
      setCats(categoriesApi.cached() || []);
      if (myShop) setShop(myShop);
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      // Три запроса параллельно, а не по очереди — ожидание не складывается
      const [s, list, catList] = await Promise.all([
        shopApi.my(),
        productsApi.list(),
        categoriesApi.list().catch(() => []),
      ]);
      setShop(s);
      setProducts(list);
      setCats(catList);
    } catch (e) { show(e.message, 'error'); }
    setLoading(false);
  };

  // Лёгкая перезагрузка товаров без полноэкранного спиннера —
  // используется после продажи / оплаты баллами для обновления остатков.
  const reloadProducts = async () => {
    try {
      const list = await productsApi.list();
      setProducts(list);
    } catch (e) { /* не критично, пользователь увидит при следующей загрузке */ }
  };

  // Обновление при возврате на страницу из фона (макс. раз в 30 сек)
  useReloadOnFocus(reloadProducts);

  // Хелпер: сборка "виртуального" объекта для корзины — товар + конкретный вариант
  // Используется когда у товара несколько вариантов: в корзине хранится конкретный вариант.
  const buildVariantItem = (product, variant) => {
    const attrParts = variant.attributeValues
      ? Object.values(variant.attributeValues).filter(Boolean)
      : [];
    const compositeName = attrParts.length > 0
      ? `${product.name} · ${attrParts.join(' / ')}`
      : product.name;
    const variantPhotos = Array.isArray(variant.photos) && variant.photos.length > 0
      ? variant.photos
      : (product.photos || []);
    return {
      id: product.id,                  // productId сохраняем для совместимости
      variantId: variant.id,
      name: compositeName,
      salePrice: variant.salePrice,
      costPrice: variant.costPrice,
      stock: variant.stock,
      unit: product.unit,
      isService: product.isService,   // услуга — чтобы касса не показывала остаток
      photo: variantPhotos[0] || product.photo || null,
      photos: variantPhotos,
      attributeValues: variant.attributeValues || {},
      components: product.components, // BOM не на уровне варианта
      // Помечаем variantId для дедупликации в корзине
      _cartKey: `${product.id}::${variant.id}`,
    };
  };

  // Активные варианты товара (исключаем дефолтный-пустышку если есть multi)
  const activeVariants = (product) => {
    if (!Array.isArray(product?.variants)) return [];
    return product.variants.filter(v => v.active !== false);
  };

  // Состояние выбора варианта — если открыт, показываем VariantPickerSheet
  const [variantPickerProduct, setVariantPickerProduct] = useState(null);

  const openAddSheet = (product) => {
    const variants = activeVariants(product);
    if (variants.length > 1) {
      // Несколько вариантов — открываем picker
      setVariantPickerProduct(product);
      return;
    }
    // Один вариант — берём его (или работаем со старым форматом без вариантов)
    const variant = variants[0];
    if (variant) {
      const variantItem = buildVariantItem(product, variant);
      setActiveProduct(variantItem);
    } else {
      // Совсем без вариантов (старый формат) — как было
      setActiveProduct(product);
    }
    setEditingItem(null);
    setQtyInput('');
    setPriceInput('');
    setActiveField('qty');
  };

  // Юзер выбрал конкретный вариант в VariantPickerSheet
  const onPickVariant = (product, variant) => {
    setVariantPickerProduct(null);
    const variantItem = buildVariantItem(product, variant);
    // Планшет: штучный вариант сразу +1, как нажатие на плитку
    if (wide && !isWeightUnit(variantItem.unit)) {
      addUnitToCart(variantItem);
      return;
    }
    setActiveProduct(variantItem);
    setEditingItem(null);
    setQtyInput('');
    setPriceInput('');
    setActiveField('qty');
  };

  // Сканирование баркода в кассе.
  // Если товар найден сразу с вариантом — сразу в qty sheet.
  // Если нашёлся product без variant — обычная логика openAddSheet (откроет picker если надо).
  // Если не найден — toast с предложением создать.
  // Мерные единицы (вводятся вручную): килограммы и литры — картофель,
  // разливное молоко и т.п. Граммы/миллилитры считаем штучными (парфюм,
  // крупа в пачке — их берут поштучно). Всё прочее — тоже штучное: скан = +1.
  const WEIGHT_UNITS = ['кг', 'kg', 'л', 'l'];
  const isWeightUnit = (u) => WEIGHT_UNITS.includes(String(u || '').trim().toLowerCase());

  // Прямое добавление одной штуки в чек (без листа количества).
  // Функциональное обновление — чтобы серия быстрых сканов не терялась.
  const addUnitToCart = (item) => {
    const cartKey = item._cartKey || item.id;
    setCart(prev => {
      const idx = prev.findIndex(i => (i._cartKey || i.id) === cartKey);
      let next;
      if (idx >= 0) {
        // +1 к уже пробитому — переносим строку в конец: «последний выбранный» сверху чека
        const bumped = { ...prev[idx], qty: (Number(prev[idx].qty) || 0) + 1 };
        next = [...prev.slice(0, idx), ...prev.slice(idx + 1), bumped];
      } else {
        next = [...prev, {
          ...item, qty: 1,
          salePrice: item.salePrice,
          originalPrice: item.salePrice,
          salePriceOverride: null,
        }];
      }
      // Тост и мягкое предупреждение об остатках — после обновления состояния.
      queueMicrotask(() => {
        show(t('cashier.added', { name: item.name }));
        const warnings = checkLowStock(next);
        if (warnings.length > 0) {
          const w = warnings[0];
          setTimeout(() => {
            show(t('products.lowStockWarning', { name: w.name, need: w.need, have: w.have }), 'error');
          }, 300);
        }
      });
      return next;
    });
  };

  // Разбор весового кода от весов: 20 + PLU(5) + граммы(5) + контрольная.
  // Возвращает { plu, grams } или null, если формат не весовой.
  const parseWeightCode = (code) => {
    const c = String(code || '').trim();
    if (!/^\d{13}$/.test(c)) return null;
    if (!c.startsWith('20')) return null;
    // контрольная цифра EAN-13
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      const d = c.charCodeAt(i) - 48;
      sum += (i % 2 === 0) ? d : d * 3;
    }
    const check = (10 - (sum % 10)) % 10;
    if (check !== (c.charCodeAt(12) - 48)) return null;
    return { plu: parseInt(c.slice(2, 7), 10), grams: parseInt(c.slice(7, 12), 10) };
  };

  // Добавить весовую позицию отдельной строкой (не склеиваем — каждое взвешивание своё).
  const addWeightToCart = (product, kg) => {
    const item = {
      ...product,
      _cartKey: `w:${product.id}:${Date.now()}:${Math.random().toString(36).slice(2, 7)}`,
      qty: kg,
      salePrice: product.salePrice,
      originalPrice: product.salePrice,
      salePriceOverride: null,
      byWeight: true,   // пометка «добавлено весами» (уходит в журнал действий)
    };
    setCart(prev => {
      const next = [...prev, item];
      queueMicrotask(() => {
        show(t('cashier.addedWeight', '{{name}} · {{kg}} кг', { name: product.name, kg: kg.toFixed(3) }));
        const warnings = checkLowStock(next);
        if (warnings.length > 0) {
          const w = warnings[0];
          setTimeout(() => show(t('products.lowStockWarning', { name: w.name, need: w.need, have: w.have }), 'error'), 300);
        }
      });
      return next;
    });
  };

  // «Ноунэйм»: товара нет в базе — продаём по свободной цене, чтобы магазин
  // мог работать, пока не вбил весь ассортимент. noName = { barcode } | null.
  // barcode — неизвестный отсканированный код (попадёт в отчёт «завести товар»).
  const [noName, setNoName] = useState(null);
  const [bankPay, setBankPay] = useState(false);   // «Безнал» → выбор QR банка

  const addNoNameToCart = (price, barcode) => {
    const item = {
      id: null,
      _cartKey: `nn:${Date.now()}:${Math.random().toString(36).slice(2, 7)}`,
      name: t('cashier.noName.item'),
      noName: true,
      scannedBarcode: barcode || null,
      isService: true,          // остатков нет — касса не проверяет склад
      unit: 'шт',
      qty: 1,
      salePrice: price,
      originalPrice: price,
      salePriceOverride: null,
      costPrice: 0,
    };
    setCart(prev => [...prev, item]);
    setNoName(null);
    show(t('cashier.added', { name: item.name }));
  };

  // Нажатие на плитку товара на планшете — как скан: штучный сразу +1
  // (количество правится −/+ в чеке справа), весовой или мультивариант — лист.
  const tapProduct = (p) => {
    const variants = activeVariants(p);
    if (variants.length > 1) { openAddSheet(p); return; }
    const item = variants[0] ? buildVariantItem(p, variants[0]) : p;
    if (isWeightUnit(item.unit)) { openAddSheet(p); return; }
    addUnitToCart(item);
  };

  // Скан на кассе: штучный товар — сразу +1; весовой или мультивариант — лист.
  const handleCashierScan = async (code) => {
    // Код «ноунэйм» из бумажного каталога — сразу ввод цены
    if (String(code || '').trim() === NONAME_BARCODE) {
      setNoName({ barcode: null });
      return;
    }
    try {
      const r = await productsApi.byBarcode(code);
      if (r?.product) {
        // Баркод сразу указал на вариант
        if (r.variant) {
          const variantItem = buildVariantItem(r.product, r.variant);
          if (isWeightUnit(variantItem.unit)) {
            setActiveProduct(variantItem);
            setEditingItem(null);
            setQtyInput('');
            setPriceInput('');
            setActiveField('qty');
          } else {
            addUnitToCart(variantItem);
          }
          return;
        }
        // Вариант не определён баркодом
        const variants = activeVariants(r.product);
        if (variants.length > 1) {
          // Несколько вариантов — без выбора не обойтись, оставляем лист/picker
          openAddSheet(r.product);
          return;
        }
        const v = variants[0];
        const item = v ? buildVariantItem(r.product, v) : r.product;
        if (isWeightUnit(item.unit)) {
          setActiveProduct(item);
          setEditingItem(null);
          setQtyInput('');
          setPriceInput('');
          setActiveField('qty');
        } else {
          addUnitToCart(item);
        }
        return;
      }
    } catch (e) {
      // 404 или ошибка — упадём ниже (возможно, это весовой код)
    }

    // (b) Не нашли как обычный штрихкод — пробуем как ВЕСОВОЙ код от весов
    const w = parseWeightCode(code);
    if (w) {
      if (!w.grams) {
        show(t('cashier.weightZero', 'Нулевой вес — положите товар на весы'), 'error');
        return;
      }
      const prod = products.find(p => p.plu != null && Number(p.plu) === w.plu);
      if (prod) {
        addWeightToCart(prod, w.grams / 1000);
      } else {
        show(t('cashier.pluNotFound', 'Товар с номером на весах {{plu}} не найден', { plu: w.plu }), 'error');
      }
      return;
    }

    // Не нашли нигде — предлагаем продать как «товар без названия»
    setNoName({ barcode: String(code || '').trim() });
  };

  const openEditSheet = (item) => {
    setEditingItem(item);
    setActiveProduct(item);
    setQtyInput(String(item.qty).replace('.', ','));
    // Если цена была переопределена — показываем её; иначе пусто
    const customPrice = item.salePriceOverride;
    setPriceInput(customPrice ? String(customPrice).replace('.', ',') : '');
    setActiveField('qty');
  };

  const closeSheet = () => {
    setActiveProduct(null);
    setEditingItem(null);
    setQtyInput('');
    setPriceInput('');
    setActiveField('qty');
  };

  // Проверка остатков компонентов после добавления в корзину
  // Считает сколько компонентов потребуется на ВСЕ товары в корзине (с новым/изменённым)
  // и показывает предупреждение если хоть одного не хватает.
  const checkLowStock = (cartAfterChange) => {
    const needed = new Map(); // productId → totalQty

    for (const it of cartAfterChange) {
      if (!Array.isArray(it.components) || it.components.length === 0) {
        // Обычный товар — сам себе нужен
        if (it.id) {
          needed.set(it.id, (needed.get(it.id) || 0) + Number(it.qty));
        }
      } else {
        // Составной — раскрываем компоненты (1 уровень, для предупреждения хватит)
        for (const c of it.components) {
          const cid = c.componentId || c.component?.id;
          if (!cid) continue;
          needed.set(cid, (needed.get(cid) || 0) + Number(c.qty) * Number(it.qty));
        }
      }
    }

    // Сравнение с остатками
    const warnings = [];
    for (const [pid, totalQty] of needed.entries()) {
      const prod = products.find(p => p.id === pid);
      if (!prod) continue;
      if (prod.isService) continue; // услуга — остаток не проверяем
      if (Number(prod.stock) < totalQty) {
        warnings.push({
          name: prod.name,
          need: totalQty,
          have: Number(prod.stock) || 0,
        });
      }
    }
    return warnings;
  };

  const confirmQty = () => {
    const qty = parseQty(qtyInput);
    if (!qty) return show(t('stockOp.errorQty'), 'error');

    // Цена: если введена кастомная — используем её, иначе из карточки
    const customPrice = parseQty(priceInput);
    const effectivePrice = customPrice != null ? customPrice : activeProduct.salePrice;
    const isOverridden = customPrice != null && customPrice !== activeProduct.salePrice;

    // Ключ дедупликации в корзине: для товаров с вариантами — productId+variantId,
    // для обычных — productId. Чтобы разные варианты одного товара были разными строками.
    const cartKey = activeProduct._cartKey || activeProduct.id;

    let nextCart;
    if (editingItem) {
      nextCart = cart.map(i => (i._cartKey || i.id) === cartKey
        ? { ...i, qty, salePrice: effectivePrice, salePriceOverride: isOverridden ? customPrice : null, originalPrice: activeProduct.salePrice }
        : i
      );
      setCart(nextCart);
      show(t('cashier.changed'));
    } else {
      const idx = cart.findIndex(i => (i._cartKey || i.id) === cartKey);
      const newItem = {
        ...activeProduct,
        qty,
        salePrice: effectivePrice,
        salePriceOverride: isOverridden ? customPrice : null,
        originalPrice: activeProduct.salePrice,
      };
      if (idx >= 0) {
        nextCart = [...cart];
        nextCart[idx] = newItem;
      } else {
        nextCart = [...cart, newItem];
      }
      setCart(nextCart);
      show(t("cashier.added", { name: activeProduct.name }));
    }

    // Проверка остатков компонентов — мягкое предупреждение, не блокирует
    const warnings = checkLowStock(nextCart);
    if (warnings.length > 0) {
      const w = warnings[0];
      setTimeout(() => {
        show(t('products.lowStockWarning', { name: w.name, need: w.need, have: w.have }), 'error');
      }, 800);
    }

    closeSheet();
  };

  const removeFromCart = (cartKey) => setCart(prev => prev.filter(i => (i._cartKey || i.id) !== cartKey));

  const clearCart = () => {
    if (cart.length && !window.confirm(t('cashier.clear') + '?')) return;
    setCart([]);
    setSaleDiscount(null);
  };

  // Применить скидку к строке корзины или к чеку.
  // target: 'sale' | { cartKey: string }
  // value: { type, value } | null (null = убрать скидку)
  const applyDiscount = (target, value) => {
    if (target === 'sale') {
      setSaleDiscount(value);
      return;
    }
    setCart(prev => prev.map(it =>
      (it._cartKey || it.id) === target.cartKey
        ? { ...it, discountType: value?.type || null, discountValue: value?.value || 0 }
        : it
    ));
  };

  // Расчёты для отображения и checkout
  const cartCalcs = cart.map(i => {
    const lineBase = i.salePrice * i.qty;
    const itemDisc = calcDiscountAmount(lineBase, i.discountType, i.discountValue);
    return { ...i, _lineBase: lineBase, _itemDisc: itemDisc, _lineTotal: lineBase - itemDisc };
  });
  const subtotal = cartCalcs.reduce((s, i) => s + i._lineTotal, 0);
  const saleDiscountAmount = calcDiscountAmount(subtotal, saleDiscount?.type, saleDiscount?.value);
  const total = Math.max(0, Math.round((subtotal - saleDiscountAmount) * 100) / 100);
  const grossSum = cartCalcs.reduce((s, i) => s + i._lineBase, 0); // до всех скидок
  const totalDiscount = grossSum - total;

  const loyaltyOn = shop?.loyaltyEnabled !== false;
  const pointsEarned = loyaltyOn ? Math.floor(total * (shop?.pointsPerSom || 1)) : 0;
  const totalItems = cart.length;

  const buildSaleItems = () => cart.map(i => ({
    productId: i.id,
    variantId: i.variantId || null,
    name: i.name,
    qty: i.qty,
    unit: i.unit,
    salePrice: i.salePrice,
    costPrice: i.costPrice || 0,
    discountType: i.discountType || null,
    discountValue: Number(i.discountValue) || 0,
    noName: !!i.noName,
    scannedBarcode: i.scannedBarcode || null,
    byWeight: !!i.byWeight,   // журнал: «добавлено весами»
  }));

  const checkout = async (payMethod = 'cash') => {
    if (!cart.length) return;
    setProcessing(true);
    try {
      const sale = await salesApi.create({
        items: buildSaleItems(),
        discountType: saleDiscount?.type || null,
        discountValue: saleDiscount?.value || 0,
        payMethod,   // 'cash' | 'cashless'
      });
      setQrResult(sale);
      setCart([]);
      setSaleDiscount(null);
      reloadProducts();
    } catch (e) { show(errorMessage(e, t), 'error'); }
    setProcessing(false);
  };

  // Продажа в долг: часть оплачена сейчас (paidNow), остаток — долг должника
  const checkoutOnCredit = async (debtorId, paidNow) => {
    if (!cart.length || !debtorId) return;
    setProcessing(true);
    try {
      const sale = await salesApi.create({
        items: buildSaleItems(),
        discountType: saleDiscount?.type || null,
        discountValue: saleDiscount?.value || 0,
        counterpartyId: debtorId,
        paidNow: Number(paidNow) || 0,
      });
      setCreditOpen(false);
      setQrResult(sale);
      setCart([]);
      setSaleDiscount(null);
      reloadProducts();
    } catch (e) { show(errorMessage(e, t), 'error'); }
    setProcessing(false);
  };

  // Оплата баллами: создаём pending запись, показываем QR, ждём что клиент отсканит
  const checkoutByPoints = async () => {
    if (!cart.length) return;
    setProcessing(true);
    try {
      const items = buildSaleItems();
      const pending = await paymentsApi.initPending({
        items,
        discountType: saleDiscount?.type || null,
        discountValue: saleDiscount?.value || 0,
      });
      setPendingPayment(pending);
      // Стартуем polling
      pollRef.current = setInterval(async () => {
        try {
          const status = await paymentsApi.getPending(pending.token);
          if (status.status === 'paid') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setPendingPayment(null);
            // Переходим на финальный экран
            setQrResult({
              paidByPoints: true,
              total: pending.amount,
              pointsRedeemed: status.pointsRedeemed || pending.amount,
              cashAmount: status.cashAmount || 0,
              loyaltyEnabled: false, // не показываем QR начисления
            });
            setCart([]);
            setSaleDiscount(null);
            reloadProducts();
          } else if (status.status === 'cancelled' || status.status === 'expired') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setPendingPayment(null);
            show(status.status === 'expired' ? t('cashier.expiredPayment') : t('cashier.cancelledPayment'), 'error');
          }
        } catch (e) {
          // Если что-то пошло не так — продолжаем опрашивать. На случай временного сбоя.
        }
      }, 2000);
    } catch (e) {
      show(e.message, 'error');
    }
    setProcessing(false);
  };

  const cancelPending = async () => {
    if (!pendingPayment) return;
    try {
      await paymentsApi.cancelPending(pendingPayment.token);
    } catch (e) {}
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPendingPayment(null);
  };

  // Чистим polling при размонтировании
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // «Окно» списка товаров (как в ShopProducts): рисуем порцию и дорисовываем
  // при прокрутке. При 2000+ товаров рендер всех карточек сразу тормозит открытие.
  const PAGE = 60;
  const [visible, setVisible] = useState(PAGE);
  const sentinelRef = useRef(null);
  useEffect(() => { setVisible(PAGE); }, [search, nsOnly, catPath, products.length]);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) setVisible(v => v + PAGE);
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  });

  // ── Горизонтальная касса (планшет / широкий экран) ──────────────
  // Слева — товары плиткой, справа — чек на всю высоту.
  // layoutPref: 'auto' — сама по ширине экрана; 'wide' / 'narrow' — выбрал
  // продавец кнопкой «Планшет» (помним на устройстве).
  const WIDE_MQ = '(min-width: 900px) and (orientation: landscape)';
  const [layoutPref, setLayoutPref] = useState(() => {
    try { return localStorage.getItem('cashierLayout') || 'auto'; } catch (e) { return 'auto'; }
  });
  const [wideScreen, setWideScreen] = useState(() =>
    typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(WIDE_MQ).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(WIDE_MQ);
    const on = () => setWideScreen(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []); // eslint-disable-line
  const wide = layoutPref === 'wide' || (layoutPref === 'auto' && wideScreen);

  // Полноэкранный режим планшета: без нижнего меню и панелей Android (+~15% места).
  // Включается сам вместе с «Планшет»; кнопка в шапке и «назад» — выход (помним на устройстве).
  // В браузере — только если «Планшет» выбран кнопкой (на компьютере меню не прячем само).
  const [fsPref, setFsPref] = useState(() => {
    try { return localStorage.getItem('cashierFullscreen') || 'on'; } catch (e) { return 'on'; }
  });
  const saveFs = (v) => { setFsPref(v); try { localStorage.setItem('cashierFullscreen', v); } catch (e) {} };
  const fullscreen = wide && fsPref !== 'off' && (isNative() || layoutPref === 'wide');
  const enterFs = () => { saveFs('on'); setImmersive(true); };   // из нажатия — браузер это разрешает
  const exitFs = () => saveFs('off');

  useEffect(() => { setImmersive(fullscreen); }, [fullscreen]);
  useEffect(() => () => { setImmersive(false); }, []);   // ушли с кассы — панели вернуть
  // Браузер: вышли из полного экрана по Esc — показываем меню
  useEffect(() => {
    if (isNative()) return undefined;
    const h = () => { if (!document.fullscreenElement && fullscreen) saveFs('off'); };
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, [fullscreen]); // eslint-disable-line
  // Аппаратная «назад» на полноэкранной кассе — сначала выйти из полного экрана
  useEffect(() => {
    if (!fullscreen) return undefined;
    const h = (e) => { e.preventDefault(); exitFs(); };
    window.addEventListener('alsatuu:back', h);
    return () => window.removeEventListener('alsatuu:back', h);
  }, [fullscreen]); // eslint-disable-line

  const toggleWide = () => {
    const next = wide ? 'narrow' : 'wide';
    setLayoutPref(next);
    try { localStorage.setItem('cashierLayout', next); } catch (e) {}
    if (next === 'wide') enterFs();
  };

  // Какая строка чека развёрнута (крупно, с −/+). По умолчанию — последняя
  // добавленная; новая позиция снова разворачивается сама.
  const [expandedKey, setExpandedKey] = useState(null);
  const lastCartKey = cart.length ? (cart[cart.length - 1]._cartKey || cart[cart.length - 1].id) : null;
  useEffect(() => { setExpandedKey(null); }, [lastCartKey]);

  // −/+ количества прямо в чеке (штучные товары)
  const changeQty = (cartKey, delta) => setCart(prev => prev.map(i =>
    (i._cartKey || i.id) === cartKey
      ? { ...i, qty: Math.max(1, Math.round(((Number(i.qty) || 0) + delta) * 1000) / 1000) }
      : i));

  const newSale = () => setQrResult(null);
  const landscape = useLandscape();

  // QR-экран после оплаты
  if (qrResult) {
    const isPointsPayment = qrResult.paidByPoints === true;
    // QR показываем и при 0 баллов — клиент по нему получает чек в приложении
    const showQr = !isPointsPayment && qrResult.loyaltyEnabled !== false;
    const head = (
      <>
        <div style={{
          width: landscape ? 56 : 72, height: landscape ? 56 : 72, borderRadius: 999,
          background: 'var(--success-soft)', color: 'var(--success)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: landscape ? 10 : 16,
        }}>
          <Icon name="check" size={landscape ? 28 : 36} stroke={2.5} />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>
          {isPointsPayment ? t('cashier.paidByPoints') : t('cashier.paid')}
        </h2>
        <p style={{ fontSize: 32, fontWeight: 800, color: 'var(--brand-primary)', fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(qrResult.total, currency)}
        </p>

        {isPointsPayment && (
          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <p style={{ fontSize: 14, color: 'var(--brand-accent)', fontWeight: 700 }}>
              {t("cashier.redeemed", { points: qrResult.pointsRedeemed?.toLocaleString() })}
            </p>
            {qrResult.cashAmount > 0 && (
              <p style={{ fontSize: 14, color: 'var(--fg)', marginTop: 6, fontWeight: 600 }}>
                {t("cashier.cashTopUp", { amount: formatMoney(qrResult.cashAmount, currency) })}
              </p>
            )}
          </div>
        )}
      </>
    );
    const qrBox = showQr && (
      <div ref={receiptQrRef} style={{
        marginTop: landscape ? 0 : 24, padding: 20,
        background: '#fff', borderRadius: 'var(--r-md)',
        boxShadow: 'var(--shadow-md)',
      }}>
        <QRCodeSVG value={JSON.stringify({ shopId: shop?.id, token: qrResult.token })} size={200} level="M" />
      </div>
    );
    const qrActions = showQr ? (
      <>
        <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 16, textAlign: 'center', maxWidth: 280 }}>
          {qrResult.pointsEarned > 0 ? t("cashier.qrHint", { points: qrResult.pointsEarned }) : t('cashier.qrHintReceipt')}
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, width: '100%', maxWidth: 320 }}>
          <button
            className="btn btn-secondary"
            onClick={saveReceiptQR}
            disabled={sharing}
            style={{ flex: 1, fontSize: 14 }}
          >
            <Icon name="download" size={16} stroke={2.2} />
            {t('cashier.saveQR')}
          </button>
          <button
            className="btn btn-primary"
            onClick={shareReceiptQR}
            disabled={sharing}
            style={{ flex: 1, fontSize: 14 }}
          >
            <Icon name="share" size={16} stroke={2.2} />
            {t('cashier.shareQR')}
          </button>
        </div>
      </>
    ) : !isPointsPayment ? (
      <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 16, textAlign: 'center', maxWidth: 280 }}>
        {t('cashier.checkComplete')}
      </p>
    ) : null;
    const newSaleBtn = (
      <button className="btn btn-primary btn-lg" onClick={newSale} style={landscape ? { marginTop: 20, maxWidth: 320 } : undefined}>
        {t("cashier.newSale")}
        <Icon name="arrowRight" size={18} stroke={2.2} />
      </button>
    );

    // Горизонтальный экран: слева итог и кнопки, справа QR — всё в один экран
    if (landscape) {
      return (
        <div className="page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 48, padding: '16px 24px', paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 340, maxWidth: '50%' }}>
            {head}
            {qrActions}
            {newSaleBtn}
          </div>
          {qrBox}
        </div>
      );
    }

    return (
      <div className="page" style={{ padding: '20px 20px', display: 'flex', flexDirection: 'column', minHeight: '100vh', paddingBottom: 'calc(20px + env(safe-area-inset-bottom))' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          {head}
          {qrBox}
          {qrActions}
        </div>
        {newSaleBtn}
      </div>
    );
  }

  // Экран ожидания оплаты баллами (после нажатия "Оплатить баллами")
  if (pendingPayment) {
    const pendingQr = (
          <div ref={paymentQrRef} style={{
            marginTop: landscape ? 0 : 24, padding: 20,
            background: '#fff', borderRadius: 'var(--r-md)',
            boxShadow: 'var(--shadow-md)',
            position: 'relative',
          }}>
            <QRCodeSVG value={pendingPayment.token} size={200} level="M" />
            {/* Пульсирующая рамка для индикации ожидания */}
            <div style={{
              position: 'absolute', inset: -4,
              borderRadius: 'calc(var(--r-md) + 4px)',
              border: '3px solid var(--brand-primary)',
              opacity: 0.6,
              animation: 'pulseRing 1.6s ease-out infinite',
              pointerEvents: 'none',
            }} />
          </div>
    );
    // На горизонтальном экране — QR справа, остальное слева (без прокрутки)
    return (
      <div className="page" style={landscape
        ? { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 48, padding: '16px 24px', paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }
        : { padding: '20px 20px', display: 'flex', flexDirection: 'column', minHeight: '100vh', paddingBottom: 'calc(20px + env(safe-area-inset-bottom))' }}>
        <div style={{ flex: landscape ? '0 1 360px' : 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: landscape ? 0 : 20 }}>
          <p className="page-subtitle" style={{ marginBottom: 6 }}>{t('cashier.waiting')}</p>
          <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 4, letterSpacing: '-0.02em' }}>
            {t('cashier.waitingTitle')}
          </h2>
          <p style={{ fontSize: 30, fontWeight: 800, color: 'var(--brand-primary)', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
            {formatMoney(pendingPayment.amount, currency)}
          </p>

          {!landscape && pendingQr}

          <div style={{ display: 'flex', gap: 8, marginTop: 16, width: '100%', maxWidth: 320 }}>
            <button
              className="btn btn-secondary"
              onClick={savePaymentQR}
              disabled={sharing}
              style={{ flex: 1, fontSize: 14 }}
            >
              <Icon name="download" size={16} stroke={2.2} />
              {t('cashier.saveQR')}
            </button>
            <button
              className="btn btn-primary"
              onClick={sharePaymentQR}
              disabled={sharing}
              style={{ flex: 1, fontSize: 14 }}
            >
              <Icon name="share" size={16} stroke={2.2} />
              {t('cashier.shareQR')}
            </button>
          </div>

          <div style={{
            marginTop: 18, display: 'flex', alignItems: 'center', gap: 8,
            color: 'var(--fg-muted)', fontSize: 13,
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: 'var(--brand-primary)',
              animation: 'blink 1.2s ease-in-out infinite',
            }} />
            {t("cashier.waitingNotice")}
          </div>

          <p style={{ fontSize: 12, color: 'var(--fg-faint)', marginTop: 16, textAlign: 'center', maxWidth: 280, lineHeight: 1.5 }}>
            {t("cashier.waitingHint")}
          </p>
          {landscape && (
            <button className="btn btn-secondary btn-lg" onClick={cancelPending} style={{ marginTop: 16, maxWidth: 320 }}>
              {t('cashier.cancelPayment')}
            </button>
          )}
        </div>
        {landscape && pendingQr}

        <style>{`
          @keyframes pulseRing {
            0%   { transform: scale(1);    opacity: 0.6; }
            70%  { transform: scale(1.08); opacity: 0; }
            100% { transform: scale(1.08); opacity: 0; }
          }
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50%      { opacity: 0.3; }
          }
        `}</style>

        {!landscape && (
          <button className="btn btn-secondary btn-lg" onClick={cancelPending}>
            {t('cashier.cancelPayment')}
          </button>
        )}
      </div>
    );
  }

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  const toggleNs = () => {
    setNsOnly(v => {
      const nv = !v;
      try { localStorage.setItem('cashierNsOnly', nv ? '1' : '0'); } catch (e) {}
      return nv;
    });
    setCatPath([]);
  };

  // Многоуровневая касса — из настроек магазина
  const multiLevel = isModuleOn(shop, 'multiLevelCashier');

  // Базовый набор товаров с учётом галочки «НС» (только несканируемые = свой баркод)
  const baseProducts = nsOnly
    ? products.filter(p => isGeneratedBarcode(p.barcode))
    : products;

  // Поиск всегда работает поверх дерева — показываем плоско найденное
  const searching = !!search.trim();
  const searchLc = search.toLowerCase();
  const filtered = searching
    ? baseProducts.filter(p =>
        p.name.toLowerCase().includes(searchLc) ||
        (p.article || '').toLowerCase().includes(searchLc)
      )
    : baseProducts;

  // Позиции корзины по id товара — чтобы карточка не искала себя перебором
  const cartById = new Map();
  for (const c of cart) if (!cartById.has(c.id)) cartById.set(c.id, c);
  // Сколько всего товара в чеке (все строки: варианты, несколько взвешиваний)
  const cartQtyById = new Map();
  for (const c of cart) cartQtyById.set(c.id, Math.round(((cartQtyById.get(c.id) || 0) + (Number(c.qty) || 0)) * 1000) / 1000);

  // ── Дерево категорий (для многоуровневого режима) ──────────────
  // Товары по категориям — один проход вместо перебора на каждую категорию.
  const byCat = new Map();
  for (const p of baseProducts) {
    const k = p.categoryId || null;
    if (!byCat.has(k)) byCat.set(k, []);
    byCat.get(k).push(p);
  }
  const catHasProducts = (catId) => byCat.has(catId);
  const rootCats = cats.filter(c => !c.parentId);
  const subOf = (parentId) => cats.filter(c => c.parentId === parentId);
  // категория «видима», если в ней или её подкатегориях есть товары
  const catVisible = (c) => catHasProducts(c.id) || subOf(c.id).some(s => catHasProducts(s.id));
  // Все товары категории вместе с подкатегориями — для счётчика и обложки плитки
  const catAllProducts = (catId) => [
    ...(byCat.get(catId) || []),
    ...subOf(catId).flatMap(s => catAllProducts(s.id)),
  ];

  // На планшете касса всегда идёт по категориям (иначе плитки — длинная лента);
  // товары без категории — отдельной плиткой «Без категории».
  const NO_CAT = '__nocat__';
  const useTree = (multiLevel || wide) && !searching;

  // Что показывать на текущем уровне многоуровневой кассы
  let levelCats = [];      // подкатегории/категории-плитки текущего уровня
  let levelProducts = [];  // товары текущего уровня
  let noCatProducts = [];  // товары без категории (только на верхнем уровне)
  if (useTree) {
    const currentId = catPath[catPath.length - 1] || null;
    if (currentId === NO_CAT) {
      levelProducts = byCat.get(null) || [];
    } else if (!currentId) {
      // верхний уровень: корневые категории (видимые) + товары без категории
      levelCats = rootCats.filter(catVisible);
      noCatProducts = byCat.get(null) || [];
    } else {
      // внутри категории: её подкатегории (видимые) + её собственные товары
      levelCats = subOf(currentId).filter(catVisible);
      levelProducts = byCat.get(currentId) || [];
    }
  }
  const catName = (id) => (id === NO_CAT ? t('catalog.noCategory', 'Без категории') : (cats.find(c => c.id === id)?.name) || '');
  // Плитка «Без категории» на планшете — только если есть и категории, и товары без них
  const noCatTile = wide && catPath.length === 0 && levelCats.length > 0 && noCatProducts.length > 0;
  if (noCatTile) noCatProducts = [];

  // Сетка карточек: на широком экране — плиткой в несколько колонок
  const listStyle = wide
    ? { padding: '0 12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', gap: 8, alignItems: 'start' }
    : { padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 8 };
  const fullRow = wide ? { gridColumn: '1 / -1' } : null;

  // Подытог / скидка на чек — общий для узкой и широкой кассы
  const totalsBlock = (totalDiscount > 0 || (canDiscount && discountsEnabled)) && (
    <div style={{
      padding: '10px 12px',
      background: 'var(--bg-subtle)',
      borderRadius: 'var(--r-sm)',
      marginBottom: 8,
      fontSize: 13,
      fontVariantNumeric: 'tabular-nums',
    }}>
      {totalDiscount > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--fg-muted)' }}>
            <span>{t('discounts.subtotal')}</span>
            <span>{formatMoney(grossSum, currency)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--brand-accent)', fontWeight: 600 }}>
            <span>{t('discounts.discount')}</span>
            <span>−{formatMoney(totalDiscount, currency)}</span>
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            fontWeight: 700, fontSize: 14, marginTop: 4,
            paddingTop: 6, borderTop: '1px solid var(--border)',
          }}>
            <span>{t('common.total') || 'Итого'}</span>
            <span>{formatMoney(total, currency)}</span>
          </div>
        </>
      )}
      {canDiscount && discountsEnabled && (
        <button
          onClick={() => setDiscountTarget('sale')}
          style={{
            width: '100%', marginTop: totalDiscount > 0 ? 8 : 0,
            padding: '8px 10px',
            background: saleDiscount ? 'var(--brand-accent-soft, var(--brand-primary-soft))' : 'var(--bg-elevated)',
            border: '1px dashed var(--border)',
            borderRadius: 'var(--r-xs)',
            color: saleDiscount ? 'var(--brand-accent)' : 'var(--fg-muted)',
            fontSize: 12, fontWeight: 600,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <Icon name="percent" size={12} />
          {saleDiscount
            ? `${t('discounts.discountOnSale')}: ${saleDiscount.type === 'percent' ? `${saleDiscount.value}%` : formatMoney(saleDiscount.value, currency)}`
            : t('discounts.discountOnSale')}
        </button>
      )}
    </div>
  );

  // Кнопки оплаты + крупная плашка «К оплате» — общие для узкой и широкой кассы.
  // compact (планшет): кнопки в один ряд, ниже ростом — чтобы чек справа помещался
  const debtOn = canSeeModule(shop, 'retailDebt', { isOwner, can });
  // QR банков продавца (настройки магазина). Есть — «Безнал» сначала спрашивает банк.
  const payQrs = Array.isArray(shop?.payQrs) ? shop.payQrs : [];
  const payCashless = () => (payQrs.length ? setBankPay(true) : checkout('cashless'));
  const renderPayBlock = (compact) => {
    const payBtn = {
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 2, padding: compact ? '7px 4px' : '12px 8px', minHeight: compact ? 0 : undefined, minWidth: 0,
    };
    const label = compact
      ? { fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }
      : { fontSize: 14 };
    const iconSize = compact ? 16 : 18;
    const count = 2 + (loyaltyOn ? 1 : 0) + (debtOn ? 1 : 0);
    return (
      <>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          padding: compact ? '8px 12px' : '10px 14px', marginBottom: 8,
          background: 'var(--brand-primary-soft)', borderRadius: 'var(--r-sm)',
          border: '1px solid var(--brand-primary)',
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {processing ? t('cashier.processing') : t('waiter.toPay', 'К оплате')}
          </span>
          <strong style={{
            fontSize: compact ? 26 : 28, fontWeight: 800, lineHeight: 1.1,
            fontFamily: 'var(--font-display)', fontVariantNumeric: 'tabular-nums', color: 'var(--fg)',
          }}>
            {formatMoney(total, currency)}
          </strong>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: compact ? `repeat(${count}, 1fr)` : '1fr 1fr', gap: compact ? 6 : 8 }}>
          <button className="btn btn-primary btn-lg" onClick={() => checkout('cash')} disabled={processing} style={payBtn}>
            <Icon name="cashier" size={iconSize} />
            <span style={label}>{t('cashier.payCash', 'Наличные')}</span>
          </button>

          <button className="btn btn-primary btn-lg" onClick={payCashless} disabled={processing} style={payBtn}>
            <Icon name="creditCard" size={iconSize} />
            <span style={label}>{t('cashier.payCashless', 'Безнал')}</span>
          </button>

          {loyaltyOn && (
            <button
              className="btn btn-secondary btn-lg"
              onClick={checkoutByPoints}
              disabled={processing}
              style={{ ...payBtn, borderColor: 'var(--brand-accent)', color: 'var(--brand-accent)' }}
            >
              <Icon name="starFilled" size={iconSize} stroke={0} />
              <span style={label}>{t('cashier.payByPointsShort', 'Баллами')}</span>
            </button>
          )}

          {debtOn && (
            <button className="btn btn-secondary btn-lg" onClick={() => setCreditOpen(true)} disabled={processing} style={payBtn}>
              <Icon name="wallet" size={iconSize} />
              <span style={label}>{t('debtors.payOnCreditShort', 'В долг')}</span>
            </button>
          )}
        </div>
      </>
    );
  };

  // Значки «цена изменена» / «скидка» у строки чека
  const lineBadges = (item, hasDisc) => (
    <>
      {item.salePriceOverride != null && (
        <span style={{
          marginLeft: 6,
          fontSize: 9, fontWeight: 700,
          background: 'var(--warning-soft)', color: 'var(--warning)',
          padding: '1px 5px', borderRadius: 4,
          verticalAlign: 'middle',
        }}>±</span>
      )}
      {hasDisc && (
        <span style={{
          marginLeft: 6,
          fontSize: 9, fontWeight: 700,
          background: 'var(--brand-accent-soft, var(--brand-primary-soft))',
          color: 'var(--brand-accent)',
          padding: '1px 5px', borderRadius: 4,
          verticalAlign: 'middle',
        }}>
          {item.discountType === 'percent' ? `−${item.discountValue}%` : `−${formatMoney(item.discountValue, currency)}`}
        </span>
      )}
    </>
  );

  // ── Широкая касса: чек справа. Последний добавленный — сверху и развёрнут.
  const renderWideCart = () => {
    const lines = [...cartCalcs].reverse();
    const openKey = (expandedKey && lines.some(i => (i._cartKey || i.id) === expandedKey))
      ? expandedKey
      : (lines[0] ? (lines[0]._cartKey || lines[0].id) : null);
    return (
      <aside style={{
        width: '35%', minWidth: 300, maxWidth: 480, flexShrink: 0,
        position: 'sticky', top: 0, alignSelf: 'flex-start',
        height: fullscreen ? '100vh' : 'calc(100vh - 64px - env(safe-area-inset-bottom))',
        display: 'flex', flexDirection: 'column',
        background: 'var(--bg-elevated)',
        borderLeft: '1px solid var(--border-subtle)',
        boxShadow: '-4px 0 20px rgba(15,23,42,0.05)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 8px', paddingTop: 'calc(10px + env(safe-area-inset-top))' }}>
          <p style={{ flex: 1, fontSize: 12, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.08em' }}>
            {t('cashier.cart').toUpperCase()}
            {cart.length > 0 && <> · {totalItems} {totalItems === 1 ? t('cashier.position') : (totalItems < 5 ? t('cashier.positions') : t('cashier.positionsMany'))}</>}
          </p>
          {loyaltyOn && cart.length > 0 && (
            <p style={{ fontSize: 11, color: 'var(--brand-accent)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>+{pointsEarned} ⭐</p>
          )}
          {cart.length > 0 && (
            <button onClick={clearCart}
              style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '6px 10px', color: 'var(--danger)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-display)' }}>
              {t('cashier.clear')}
            </button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {lines.length === 0 && (
            <div style={{ margin: 'auto 0', textAlign: 'center', color: 'var(--fg-faint)', padding: 20 }}>
              <Icon name="cart" size={32} stroke={1.5} />
              <p style={{ fontSize: 14, marginTop: 8, lineHeight: 1.5 }}>{t('cashier.wideEmpty')}</p>
            </div>
          )}
          {lines.map(item => {
            const hasDisc = item.discountType && Number(item.discountValue) > 0;
            const cartKey = item._cartKey || item.id;
            const isOpen = cartKey === openKey;
            const piece = !isWeightUnit(item.unit);
            if (!isOpen) {
              return (
                <div key={cartKey} onClick={() => setExpandedKey(cartKey)} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', flexShrink: 0,
                  background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)', cursor: 'pointer',
                }}>
                  <p style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.name}{lineBadges(item, hasDisc)}
                  </p>
                  <span style={{ fontSize: 13, color: 'var(--fg-muted)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    {item.qty} {unitLabel(item.unit)}
                  </span>
                  <strong style={{ fontSize: 14, fontVariantNumeric: 'tabular-nums', flexShrink: 0, minWidth: 70, textAlign: 'right' }}>
                    {formatMoney(item._lineTotal, currency)}
                  </strong>
                </div>
              );
            }
            // Текущая позиция — компактно: название+цена, затем −/кол-во/+ … сумма одной строкой
            const smallQty = { ...qtyBtn, width: 40, height: 40 };
            return (
              <div key={cartKey} style={{
                padding: '8px 10px', borderRadius: 'var(--r-md)', flexShrink: 0,
                border: '2px solid var(--brand-primary)', background: 'var(--bg-elevated)',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontSize: 15, fontWeight: 700, lineHeight: 1.25,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>
                      {item.name}{lineBadges(item, hasDisc)}
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--fg-muted)', fontVariantNumeric: 'tabular-nums' }}>
                      {formatMoney(item.salePrice, currency)} / {unitLabel(item.unit)}
                    </p>
                  </div>
                  <button onClick={() => removeFromCart(cartKey)} style={{ ...iconBtn, width: 32, height: 32 }} aria-label={t('common.delete')}>
                    <Icon name="x" size={15} />
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                  {piece && (
                    <button onClick={() => changeQty(cartKey, -1)} disabled={Number(item.qty) <= 1}
                      style={{ ...smallQty, opacity: Number(item.qty) <= 1 ? 0.4 : 1 }} aria-label="−">
                      <Icon name="minus" size={18} stroke={2.5} />
                    </button>
                  )}
                  <button onClick={() => openEditSheet(item)}
                    style={{ ...smallQty, width: 'auto', minWidth: 60, padding: '0 10px', fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                    {item.qty} {unitLabel(item.unit)}
                  </button>
                  {piece && (
                    <button onClick={() => changeQty(cartKey, 1)} style={smallQty} aria-label="+">
                      <Icon name="plus" size={18} stroke={2.5} />
                    </button>
                  )}
                  {canDiscount && discountsEnabled && (
                    <button onClick={() => setDiscountTarget({ cartKey })}
                      style={{ ...iconBtn, width: 32, height: 32, color: hasDisc ? 'var(--brand-accent)' : 'var(--fg-muted)' }}
                      aria-label={t('discounts.discountTitle')}>
                      <Icon name="percent" size={14} />
                    </button>
                  )}
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {hasDisc && <s style={{ display: 'block', fontSize: 11, color: 'var(--fg-faint)', lineHeight: 1.1 }}>{formatMoney(item._lineBase, currency)}</s>}
                    <strong style={{ fontSize: 18, fontFamily: 'var(--font-display)', whiteSpace: 'nowrap' }}>{formatMoney(item._lineTotal, currency)}</strong>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {cart.length > 0 && (
          <div style={{ padding: '8px 12px 10px', borderTop: '1px solid var(--border-subtle)', flexShrink: 0 }}>
            {totalsBlock}
            {renderPayBlock(true)}
          </div>
        )}
      </aside>
    );
  };

  // Кнопки «Свободная цена» / «НС» / «Планшет»: в узкой кассе — в шапке, в широкой — в строке поиска
  const toolButtons = (
    <>
        <button onClick={() => setNoName({ barcode: null })}
          title={t('cashier.noName.btnHint')}
          style={{
            marginLeft: 8, flexShrink: 0,
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)', padding: '8px 12px',
            color: 'var(--fg-muted)', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'var(--font-display)',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
          <Icon name="plus" size={14} stroke={2.5} />
          {t('cashier.noName.btn')}
        </button>
        {isModuleOn(shop, 'ownBarcodes') && (
          <button onClick={toggleNs}
            title={t('cashier.nsHint', 'Только несканируемые товары')}
            style={{
              marginLeft: 8, flexShrink: 0,
              background: nsOnly ? 'var(--brand-primary)' : 'var(--bg-elevated)',
              border: `1px solid ${nsOnly ? 'var(--brand-primary)' : 'var(--border)'}`,
              borderRadius: 'var(--r-sm)', padding: '8px 12px',
              color: nsOnly ? '#fff' : 'var(--fg-muted)', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'var(--font-display)',
            }}>
            {t('cashier.ns', 'НС')}
          </button>
        )}
        <button onClick={toggleWide}
          title={t('cashier.wideHint')}
          style={{
            marginLeft: 8, flexShrink: 0,
            background: wide ? 'var(--brand-primary)' : 'var(--bg-elevated)',
            border: `1px solid ${wide ? 'var(--brand-primary)' : 'var(--border)'}`,
            borderRadius: 'var(--r-sm)', padding: '8px 12px',
            color: wide ? '#fff' : 'var(--fg-muted)', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'var(--font-display)',
          }}>
          {t('cashier.wideBtn')}
        </button>
        {wide && (
          <button onClick={fullscreen ? exitFs : enterFs}
            title={fullscreen ? t('cashier.fsExit') : t('cashier.fsEnter')}
            aria-label={fullscreen ? t('cashier.fsExit') : t('cashier.fsEnter')}
            style={{
              marginLeft: 8, flexShrink: 0, width: 40, alignSelf: 'stretch', minHeight: 34,
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-sm)', color: 'var(--fg)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
            <Icon name={fullscreen ? 'minimize' : 'maximize'} size={18} stroke={2} />
          </button>
        )}
    </>
  );

  return (
    <div className="page" style={{ paddingBottom: fullscreen ? 16 : wide ? 80 : (cart.length ? (loyaltyOn ? 380 : 320) : 100) }}>
      {ToastView}

      <div style={wide ? { display: 'flex', alignItems: 'flex-start' } : undefined}>
      <div style={wide ? { flex: 1, minWidth: 0 } : undefined}>
      {!wide && (
      <div className="page-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="page-subtitle">{t('cashier.subtitle')}</p>
          <h1 className="page-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {shop?.name || t('cashier.subtitle')}
          </h1>
        </div>
        {!wide && cart.length > 0 && (
          <button onClick={clearCart}
            style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-sm)', padding: '8px 14px',
              color: 'var(--danger)', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'var(--font-display)',
            }}>
            {t('cashier.clear')}
          </button>
        )}
        {!wide && toolButtons}
      </div>
      )}

      <div style={wide
        ? { padding: '8px 12px', paddingTop: 'calc(8px + env(safe-area-inset-top))', display: 'flex', gap: 6, alignItems: 'center' }
        : { padding: '0 20px 12px', display: 'flex', gap: 8 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          flex: 1, minWidth: 0,
          height: wide ? 40 : 48, padding: wide ? '0 12px' : '0 16px',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
        }}>
          <Icon name="search" size={18} color="var(--fg-faint)" />
          <input
            placeholder={t("products.search")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              all: 'unset', flex: 1, fontSize: 15,
              fontFamily: 'inherit', color: 'var(--fg)',
            }}
          />
        </div>
        <ScanButton onScan={handleCashierScan} size={wide ? 'md' : 'lg'} />
        {wide && toolButtons}
      </div>

      {(() => {
        // Квадратная плитка товара (планшет): крупное фото, название, цена
        const renderProductTile = (p) => {
          const inCart = cartById.get(p.id);
          const photo = getMainPhoto(p);
          return (
            <button
              key={p.id}
              onClick={() => tapProduct(p)}
              className="card"
              style={{
                position: 'relative', cursor: 'pointer', padding: 0, overflow: 'hidden',
                display: 'flex', flexDirection: 'column', textAlign: 'left', width: '100%',
                border: inCart ? '2px solid var(--brand-primary)' : '1px solid var(--border-subtle)',
                fontFamily: 'inherit', color: 'var(--fg)',
              }}
            >
              <div style={{ height: TILE_PHOTO_H, width: '100%', background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-faint)' }}>
                {photo
                  ? <img src={photo} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  : <Icon name="package" size={28} stroke={1.5} />}
              </div>
              <div style={{ padding: '5px 8px 6px' }}>
                <p style={{
                  fontSize: 12, fontWeight: 600, lineHeight: 1.2, height: '2.4em', overflow: 'hidden',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>{p.name}</p>
                <p style={{ marginTop: 2, fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>
                  {p.salePrice}
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-faint)', marginLeft: 4 }}>
                    {getCurrencySymbol(currency)}{isWeightUnit(p.unit) ? ` / ${unitLabel(p.unit)}` : ''}
                  </span>
                </p>
              </div>
              {inCart && (
                <div style={{ position: 'absolute', top: 4, right: 4, background: 'var(--brand-primary)', color: '#fff', borderRadius: 'var(--r-full)', minWidth: 24, height: 24, fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px', fontFamily: 'var(--font-display)', fontVariantNumeric: 'tabular-nums', boxShadow: '0 1px 4px rgba(0,0,0,.25)' }}>
                  {cartQtyById.get(p.id)}{isWeightUnit(p.unit) ? ` ${unitLabel(p.unit)}` : ''}
                </div>
              )}
            </button>
          );
        };

        // Квадратная плитка категории (планшет): обложка — фото товара из неё
        const renderCatSquare = (c) => {
          const items = c.id === NO_CAT ? (byCat.get(null) || []) : catAllProducts(c.id);
          const cover = items.map(getMainPhoto).find(Boolean);
          return (
            <button key={c.id} onClick={() => setCatPath(prev => [...prev, c.id])}
              className="card"
              style={{
                position: 'relative', cursor: 'pointer', padding: 0, overflow: 'hidden',
                display: 'flex', flexDirection: 'column', textAlign: 'left', width: '100%',
                border: '1px solid var(--border-subtle)', fontFamily: 'inherit', color: 'var(--fg)',
              }}
            >
              <div style={{ height: TILE_PHOTO_H, width: '100%', background: 'var(--brand-primary-soft, var(--bg-subtle))', color: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {cover
                  ? <img src={cover} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  : <Icon name="folder" size={32} stroke={1.5} />}
              </div>
              <div style={{ position: 'absolute', top: 4, left: 4, background: 'rgba(0,0,0,.6)', color: '#fff', borderRadius: 'var(--r-xs)', padding: '2px 6px', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700 }}>
                <Icon name="folder" size={12} /> {items.length}
              </div>
              <div style={{ padding: '5px 8px 6px' }}>
                <p style={{
                  fontSize: 13, fontWeight: 700, lineHeight: 1.2, height: '2.4em', overflow: 'hidden',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>{c.name}</p>
              </div>
            </button>
          );
        };

        // Карточка товара — общая для всех режимов (на планшете — плитка)
        const renderProductCard = (p) => {
          if (wide) return renderProductTile(p);
          const inCart = cartById.get(p.id);
          return (
            <div
              key={p.id}
              onClick={() => openAddSheet(p)}
              className="card"
              style={{
                cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'center', padding: 12,
                border: inCart ? '2px solid var(--brand-primary)' : '1px solid var(--border-subtle)',
                transition: 'transform 120ms var(--ease-spring)',
              }}
            >
              {getMainPhoto(p) ? (
                <img src={getMainPhoto(p)} alt="" loading="lazy" decoding="async" style={{ width: 48, height: 48, borderRadius: 'var(--r-sm)', objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <div style={{ width: 48, height: 48, borderRadius: 'var(--r-sm)', background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-faint)', flexShrink: 0 }}>
                  <Icon name="package" size={20} />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2 }}>
                  {p.isService ? t('products.serviceTag')
                    : isComposite(p) ? t('products.canAssemble', { count: computeAvailable(p, products) })
                    : t("stockOp.stockLeft", { qty: p.stock, unit: unitLabel(p.unit) })}
                </p>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <p style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)' }}>{p.salePrice}</p>
                <p style={{ fontSize: 10, color: 'var(--fg-faint)' }}>{getCurrencySymbol(currency)} / {unitLabel(p.unit)}</p>
              </div>
              {inCart && (
                <div style={{ background: 'var(--brand-primary)', color: '#fff', borderRadius: 'var(--r-full)', minWidth: 26, height: 26, fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px', flexShrink: 0, fontFamily: 'var(--font-display)', fontVariantNumeric: 'tabular-nums' }}>
                  {inCart.qty} {unitLabel(inCart.unit)}
                </div>
              )}
            </div>
          );
        };

        // Плитка категории
        const renderCatTile = (c) => (
          <button key={c.id} onClick={() => setCatPath(prev => [...prev, c.id])}
            className="card"
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, padding: 14, border: '1px solid var(--border-subtle)', textAlign: 'left', width: '100%' }}
          >
            <div style={{ width: 44, height: 44, borderRadius: 'var(--r-sm)', background: 'var(--brand-primary-soft, var(--bg-subtle))', color: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name="folder" size={22} />
            </div>
            <span style={{ flex: 1, fontSize: 15, fontWeight: 700 }}>{c.name}</span>
            <Icon name="chevronRight" size={18} color="var(--fg-faint)" />
          </button>
        );

        // МНОГОУРОВНЕВЫЙ режим (без активного поиска)
        if (useTree) {
          const nothingHere = levelCats.length === 0 && levelProducts.length === 0 && noCatProducts.length === 0 && !noCatTile;
          const renderCat = wide ? renderCatSquare : renderCatTile;
          return (
            <div style={listStyle}>
              {/* Хлебные крошки / назад (на планшете — крупнее, под палец) */}
              {catPath.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: wide ? 12 : 8, marginBottom: 4, ...fullRow }}>
                  <button onClick={() => setCatPath(prev => prev.slice(0, -1))}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: wide ? '8px 14px' : '6px 10px', cursor: 'pointer', fontSize: wide ? 15 : 13, fontWeight: wide ? 700 : undefined, color: 'var(--fg)' }}>
                    <Icon name="chevronLeft" size={wide ? 20 : 16} /> {t('common.back', 'Назад')}
                  </button>
                  <span style={{ fontSize: wide ? 16 : 13, color: 'var(--fg-muted)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {catPath.map(catName).filter(Boolean).join(' · ')}
                  </span>
                </div>
              )}

              {nothingHere ? (
                <div className="empty-state" style={fullRow}>
                  <div className="icon"><Icon name="package" size={28} stroke={1.5} /></div>
                  <p>{nsOnly ? t('cashier.noNsProducts', 'Нет несканируемых товаров') : t('cashier.noProducts')}</p>
                </div>
              ) : (
                <>
                  {levelCats.map(renderCat)}
                  {noCatTile && renderCatSquare({ id: NO_CAT, name: catName(NO_CAT) })}
                  {levelProducts.slice(0, visible).map(renderProductCard)}
                  {/* Товары без категории — только на верхнем уровне */}
                  {catPath.length === 0 && noCatProducts.length > 0 && (
                    <>
                      {levelCats.length > 0 && (
                        <p style={{ fontSize: 12, color: 'var(--fg-faint)', margin: '8px 2px 2px', fontWeight: 700, ...fullRow }}>
                          {t('catalog.noCategory', 'Без категории')}
                        </p>
                      )}
                      {noCatProducts.slice(0, visible).map(renderProductCard)}
                    </>
                  )}
                  {(visible < levelProducts.length || visible < noCatProducts.length) && (
                    <div ref={sentinelRef} style={{ height: 1, ...fullRow }} />
                  )}
                </>
              )}
            </div>
          );
        }

        // ПЛОСКИЙ режим (как раньше) — либо поиск, либо многоуровневый выключен
        if (filtered.length === 0) {
          return (
            <div className="empty-state">
              <div className="icon"><Icon name="package" size={28} stroke={1.5} /></div>
              <p>{search ? t('products.noProductsFound') : (nsOnly ? t('cashier.noNsProducts', 'Нет несканируемых товаров') : t('cashier.noProducts'))}</p>
            </div>
          );
        }
        return (
          <div style={listStyle}>
            {filtered.slice(0, visible).map(renderProductCard)}
            {visible < filtered.length && (
              <div ref={sentinelRef} style={{ height: 1, ...fullRow }} />
            )}
          </div>
        );
      })()}
      </div>
      {wide && renderWideCart()}
      </div>

      {/* Корзина (узкая касса — снизу над таб-баром) */}
      {!wide && cart.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 'calc(64px + env(safe-area-inset-bottom))', /* выше таб-бара */
          left: 0, right: 0,
          background: 'var(--bg-elevated)',
          borderTop: '1px solid var(--border-subtle)',
          padding: 14,
          zIndex: 50,
          maxHeight: '50vh',
          overflowY: 'auto',
          boxShadow: '0 -4px 20px rgba(15,23,42,0.08)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.08em' }}>
              {t("cashier.cart")} · {totalItems} {totalItems === 1 ? t("cashier.position") : (totalItems < 5 ? t("cashier.positions") : t("cashier.positionsMany"))}
            </p>
            {loyaltyOn && (
              <p style={{ fontSize: 11, color: 'var(--brand-accent)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                +{pointsEarned} ⭐
              </p>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            {cartCalcs.map(item => {
              const hasDisc = item.discountType && Number(item.discountValue) > 0;
              const cartKey = item._cartKey || item.id;
              return (
                <div key={cartKey} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px',
                  background: 'var(--bg-subtle)',
                  borderRadius: 'var(--r-sm)',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.name}
                      {lineBadges(item, hasDisc)}
                    </p>
                    <p style={{ fontSize: 11, color: 'var(--fg-faint)', fontVariantNumeric: 'tabular-nums' }}>
                      {item.qty} {unitLabel(item.unit)} × {item.salePrice}
                      {hasDisc ? (
                        <> = <s style={{ color: 'var(--fg-faint)' }}>{formatMoney(item._lineBase, currency)}</s>
                          <strong style={{ color: 'var(--fg)', marginLeft: 6 }}>{formatMoney(item._lineTotal, currency)}</strong></>
                      ) : (
                        <> = <strong style={{ color: 'var(--fg)' }}>{formatMoney(item._lineTotal, currency)}</strong></>
                      )}
                    </p>
                  </div>
                  {canDiscount && discountsEnabled && (
                    <button
                      onClick={() => setDiscountTarget({ cartKey })}
                      style={{ ...iconBtn, color: hasDisc ? 'var(--brand-accent)' : 'var(--fg-muted)' }}
                      aria-label={t('discounts.discountTitle')}
                    >
                      <Icon name="percent" size={14} />
                    </button>
                  )}
                  <button onClick={() => openEditSheet(item)} style={iconBtn} aria-label={t("common.edit")}>
                    <Icon name="edit" size={14} />
                  </button>
                  <button onClick={() => removeFromCart(cartKey)} style={iconBtn} aria-label={t("common.delete")}>
                    <Icon name="x" size={14} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Блок Подытог / Скидка / Итого */}
          {totalsBlock}

          {renderPayBlock(false)}
        </div>
      )}

      {variantPickerProduct && (
        <VariantPickerSheet
          product={variantPickerProduct}
          currency={currency}
          onPick={(variant) => onPickVariant(variantPickerProduct, variant)}
          onClose={() => setVariantPickerProduct(null)}
        />
      )}

      {creditOpen && (
        <DebtorPickerSheet
          total={total}
          currency={currency}
          processing={processing}
          onConfirm={checkoutOnCredit}
          onClose={() => setCreditOpen(false)}
        />
      )}

      {activeProduct && (
        <QtySheet
          product={activeProduct}
          allProducts={products}
          isEditing={!!editingItem}
          qtyInput={qtyInput}
          setQtyInput={setQtyInput}
          priceInput={priceInput}
          setPriceInput={setPriceInput}
          activeField={activeField}
          setActiveField={setActiveField}
          inputRef={qtyInputRef}
          onConfirm={confirmQty}
          onClose={closeSheet}
          currency={currency}
        />
      )}

      {bankPay && (
        <BankQrSheet
          qrs={payQrs}
          total={total}
          currency={currency}
          processing={processing}
          onPaid={async () => { await checkout('cashless'); setBankPay(false); }}
          onClose={() => setBankPay(false)}
        />
      )}

      {noName && (
        <NoNameSheet
          barcode={noName.barcode}
          currency={currency}
          onConfirm={(price) => addNoNameToCart(price, noName.barcode)}
          onClose={() => setNoName(null)}
        />
      )}

      {discountTarget && (
        <DiscountSheet
          target={discountTarget}
          cart={cart}
          saleDiscount={saleDiscount}
          subtotal={subtotal}
          maxPercent={maxDiscPercent}
          maxAmount={maxDiscAmount}
          isOwner={isOwner}
          currency={currency}
          onApply={(value) => { applyDiscount(discountTarget, value); setDiscountTarget(null); }}
          onClose={() => setDiscountTarget(null)}
          showError={(msg) => show(msg, 'error')}
        />
      )}

      {!fullscreen && <BottomNav items={navItems} />}
    </div>
  );
}

// Высота фото в плитке планшета — от высоты экрана, чтобы влезало 3–4 ряда
const TILE_PHOTO_H = 'clamp(60px, 12vh, 120px)';

const iconBtn = {
  width: 28, height: 28, background: 'var(--bg-elevated)',
  border: '1px solid var(--border)', borderRadius: 'var(--r-xs)',
  cursor: 'pointer', color: 'var(--fg-muted)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
};

// Горизонтальный экран (планшет / телефон боком): листы и экран «Оплачено»
// раскладываются в две колонки, чтобы кнопка подтверждения влезала без прокрутки.
const LANDSCAPE_MQ = '(orientation: landscape) and (min-width: 640px)';
function useLandscape() {
  const [on, setOn] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(LANDSCAPE_MQ).matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(LANDSCAPE_MQ);
    const h = () => setOn(mq.matches);
    h();
    if (mq.addEventListener) mq.addEventListener('change', h); else mq.addListener(h);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', h); else mq.removeListener(h); };
  }, []);
  return on;
}

// Лист в горизонтальном режиме — по центру, не на всю ширину
const landscapeSheet = { maxWidth: 820, margin: '0 auto', maxHeight: '96vh', borderRadius: '20px 20px 0 0', padding: '14px 16px', paddingBottom: 'calc(14px + env(safe-area-inset-bottom))' };
// Кнопка подтверждения прилипает к низу листа (видна даже если лист прокручивается)
const stickyConfirm = { position: 'sticky', bottom: 0, background: 'var(--bg-elevated)', paddingTop: 8, marginTop: 4, zIndex: 1 };

// Крупные кнопки −/количество/+ в развёрнутой строке широкого чека (под палец)
const qtyBtn = {
  width: 48, height: 48, background: 'var(--bg-subtle)',
  border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
  cursor: 'pointer', color: 'var(--fg)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
};


// ─── «Безнал»: выбор банка → QR продавца с вписанной суммой ──────
// Банк не сообщает о переводе — кассир смотрит уведомление в своём телефоне и жмёт «Оплачено».
function BankQrSheet({ qrs, total, currency, processing, onPaid, onClose }) {
  const { t } = useTranslation();
  const landscape = useLandscape();
  const [picked, setPicked] = useState(null);
  const withSum = picked ? payQrWithAmount(picked.raw, total) : null;

  const head = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      {picked && (
        <button className="modal-close" onClick={() => setPicked(null)} aria-label={t('common.back')}>
          <Icon name="chevronLeft" size={16} />
        </button>
      )}
      <p style={{ flex: 1, fontSize: 17, fontWeight: 800, fontFamily: 'var(--font-display)' }}>
        {picked ? picked.bank || t('payQr.bank') : t('payQr.chooseBank')}
      </p>
      <button className="modal-close" onClick={onClose}><Icon name="x" size={16} /></button>
    </div>
  );

  const amount = (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
      padding: '10px 14px', marginBottom: 12, borderRadius: 'var(--r-sm)',
      background: 'var(--brand-primary-soft)', border: '1px solid var(--brand-primary)',
    }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {t('waiter.toPay', 'К оплате')}
      </span>
      <strong style={{ fontSize: 28, fontWeight: 800, fontFamily: 'var(--font-display)', fontVariantNumeric: 'tabular-nums' }}>
        {formatMoney(total, currency)}
      </strong>
    </div>
  );

  let body;
  if (!picked) {
    body = (
      <>
        {amount}
        <div style={{ display: 'grid', gridTemplateColumns: landscape ? '1fr 1fr' : '1fr', gap: 8 }}>
          {qrs.map(q => (
            <button key={q.id || q.raw} onClick={() => setPicked(q)} style={bankBtn}>
              <Icon name="creditCard" size={22} />
              <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <span style={{ display: 'block', fontSize: 16, fontWeight: 800 }}>{q.bank || t('payQr.bank')}</span>
                {q.recipient && <span style={{ display: 'block', fontSize: 12, color: 'var(--fg-muted)', fontWeight: 500 }}>{q.recipient}</span>}
              </span>
              <Icon name="chevronRight" size={18} />
            </button>
          ))}
          <button onClick={onPaid} disabled={processing} style={{ ...bankBtn, background: 'var(--bg-subtle)' }}>
            <Icon name="cashier" size={22} />
            <span style={{ flex: 1, textAlign: 'left', fontSize: 15, fontWeight: 700 }}>{t('payQr.noQr')}</span>
          </button>
        </div>
      </>
    );
  } else {
    const qr = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <div style={{ background: '#fff', padding: 12, borderRadius: 'var(--r-md)', lineHeight: 0 }}>
          <QRCodeSVG value={withSum || picked.raw} size={landscape ? 260 : 240} level="M" />
        </div>
        {!withSum && (
          <p style={{ fontSize: 13, color: 'var(--warning)', fontWeight: 700, textAlign: 'center', maxWidth: 280 }}>
            {t('payQr.noAmountHint', { amount: formatMoney(total, currency) })}
          </p>
        )}
      </div>
    );
    const side = (
      <>
        {amount}
        <p style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--fg-muted)', marginBottom: 12 }}>{t('payQr.cashierHint')}</p>
        <div style={{ flex: 1 }} />
        <div style={stickyConfirm}>
          <button className="btn btn-primary btn-lg" onClick={onPaid} disabled={processing}>
            {processing ? t('cashier.processing') : t('payQr.paid')}
          </button>
        </div>
      </>
    );
    body = landscape ? (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'stretch' }}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>{side}</div>
        {qr}
      </div>
    ) : (
      <>
        {qr}
        <div style={{ height: 12 }} />
        {side}
      </>
    );
  }

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-sheet" style={landscape ? landscapeSheet : undefined}>
        {head}
        {body}
      </div>
    </div>
  );
}

const bankBtn = {
  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
  padding: '14px 14px', minHeight: 60,
  background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
  color: 'var(--fg)', cursor: 'pointer', fontFamily: 'var(--font-display)',
};

// ─── «Ноунэйм»: цена товара, которого нет в базе ──────────────
// Одним шагом: продавец видит, что кода нет в базе, вводит цену — и в чек.
function NoNameSheet({ barcode, currency, onConfirm, onClose }) {
  const { t } = useTranslation();
  const [price, setPrice] = useState('');
  const value = parseQty(price) || 0;
  const landscape = useLandscape();

  const info = (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 'var(--r-sm)', background: 'var(--bg-subtle)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-faint)', flexShrink: 0,
          }}>
            <Icon name="package" size={24} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25, fontFamily: 'var(--font-display)' }}>
              {t('cashier.noName.item')}
            </p>
            {barcode && (
              <p style={{ fontSize: 12, color: 'var(--fg-muted)', fontVariantNumeric: 'tabular-nums' }}>{barcode}</p>
            )}
          </div>
          <button className="modal-close" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>

        <p style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--fg)', background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)', padding: '10px 12px', marginBottom: 12 }}>
          {barcode ? t('cashier.noName.notFound') : t('cashier.noName.hint')}
        </p>

        {/* В сетку, как в QtySheet, — иначе плитка ужимается по содержимому */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr' }}>
          <FieldTile
            active
            onClick={() => {}}
            label={t('cashier.qtySheet.price')}
            value={price}
            placeholder="0"
            suffix={getCurrencySymbol(currency)}
          />
        </div>
      </>
  );

  const pad = <NumPad value={price} onChange={setPrice} allowDecimal />;

  const confirm = (
        <div style={stickyConfirm}>
          <button className="btn btn-primary" onClick={() => value > 0 && onConfirm(value)} disabled={!(value > 0)} style={{ marginTop: 4 }}>
            {value > 0
              ? t('cashier.noName.add', { price: formatMoney(value, currency) })
              : t('cashier.noName.enterPrice')}
          </button>
        </div>
  );

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-sheet" style={landscape ? landscapeSheet : undefined}>
        {landscape ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'stretch' }}>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              {info}
              <div style={{ flex: 1 }} />
              {confirm}
            </div>
            <div style={{ alignSelf: 'end' }}>{pad}</div>
          </div>
        ) : (
          <>
            {info}
            <div style={{ height: 10 }} />
            {pad}
            {confirm}
          </>
        )}
      </div>
    </div>
  );
}

function QtySheet({ product, allProducts = [], isEditing, qtyInput, setQtyInput, priceInput, setPriceInput, activeField, setActiveField, inputRef, onConfirm, onClose, currency }) {

  const { t } = useTranslation();
  const qty = parseQty(qtyInput) || 0;

  // Цена: если введена кастомная — используем её
  const customPrice = parseQty(priceInput);
  const effectivePrice = customPrice != null ? customPrice : (product.salePrice || 0);
  const isOverridden = customPrice != null && customPrice !== product.salePrice;

  const subtotal = qty * effectivePrice;

  // Для составных товаров вместо stock используем computed available
  const isCompositeProduct = isComposite(product);
  const availableForComposite = isCompositeProduct ? computeAvailable(product, allProducts) : null;
  const effectiveStock = isCompositeProduct ? availableForComposite : (product.stock || 0);
  const overstock = !product.isService && qty > effectiveStock;
  const unitL = unitLabel(product.unit);
  const isPiece = unitL === 'шт';

  // Текущее значение клавиатуры — то, что в активном поле
  const currentValue = activeField === 'qty' ? qtyInput : priceInput;
  const setCurrentValue = activeField === 'qty' ? setQtyInput : setPriceInput;
  const landscape = useLandscape();

  const header = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: landscape ? 10 : 16 }}>
          {getMainPhoto(product) ? (
            <img src={getMainPhoto(product)} alt=""
              style={{ width: 56, height: 56, borderRadius: 'var(--r-sm)', objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <div style={{
              width: 56, height: 56, borderRadius: 'var(--r-sm)',
              background: 'var(--bg-subtle)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--fg-faint)', flexShrink: 0,
            }}>
              <Icon name="package" size={24} />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25, marginBottom: 2, fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }}>
              {product.name}
            </p>
            <p style={{ fontSize: 12, color: 'var(--fg-muted)', fontVariantNumeric: 'tabular-nums' }}>
              {isCompositeProduct
                ? `${formatMoney(product.salePrice, currency)} · ${t('products.canAssemble', { count: availableForComposite })}`
                : product.isService
                  ? formatMoney(product.salePrice, currency)
                  : t("cashier.qtySheet.perUnit", { price: product.salePrice, unit: unitL, stock: product.stock })}
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>
  );

  const fields = (
        /* Два переключаемых поля: количество и цена */
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: landscape ? 0 : 12 }}>
          <FieldTile
            active={activeField === 'qty'}
            onClick={() => setActiveField('qty')}
            label={t("cashier.qtySheet.qty")}
            value={qtyInput}
            placeholder="0"
            suffix={unitL}
          />
          <FieldTile
            active={activeField === 'price'}
            onClick={() => setActiveField('price')}
            label={isOverridden ? t("cashier.qtySheet.priceEdited") : t("cashier.qtySheet.price")}
            value={priceInput}
            placeholder={String(product.salePrice || 0)}
            suffix={getCurrencySymbol(currency)}
            edited={isOverridden}
          />
        </div>
  );

  const pad = (
        /* Числовая клавиатура — пишет в активное поле */
        <NumPad
          value={currentValue}
          onChange={setCurrentValue}
          allowDecimal={activeField === 'price' || !isPiece}
        />
  );

  const extras = (
      <>
        {/* Кнопка сброса цены к каталожной */}
        {priceInput !== '' && (
          <button
            type="button"
            onClick={() => { setPriceInput(''); setActiveField('qty'); }}
            style={{
              marginTop: 8, padding: '6px 10px',
              background: 'var(--bg-subtle)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-xs)',
              fontSize: 12, color: 'var(--fg-muted)', fontWeight: 600,
              cursor: 'pointer', fontFamily: 'var(--font-display)',
              width: '100%',
            }}
          >
            {t("cashier.qtySheet.resetPrice", { price: formatMoney(product.salePrice, currency) })}
          </button>
        )}

        {/* Предупреждение про остаток */}
        {overstock && qty > 0 && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: 12, marginTop: 12,
            background: 'var(--warning-soft)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: 'var(--r-sm)',
            color: 'var(--warning)',
            fontSize: 13, lineHeight: 1.4,
          }}>
            <Icon name="alert" size={18} stroke={2} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              {isCompositeProduct
                ? t("products.canAssemble", { count: availableForComposite })
                : t("cashier.qtySheet.overstock", { stock: product.stock, unit: unitL })}
            </span>
          </div>
        )}

        {/* Live-расчёт */}
        {qty > 0 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 14px', marginTop: 12,
            background: isOverridden ? 'var(--warning-soft)' : 'var(--brand-primary-soft)',
            borderRadius: 'var(--r-sm)',
            marginBottom: 12,
          }}>
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', fontVariantNumeric: 'tabular-nums' }}>
              {qty} {unitL} × {effectivePrice} {getCurrencySymbol(currency)}
              {isOverridden && (
                <span style={{ fontSize: 11, color: 'var(--warning)', display: 'block', marginTop: 2 }}>
                  {t("cashier.qtySheet.wasPrice", { price: product.salePrice })}
                </span>
              )}
            </p>
            <p style={{
              fontSize: 20, fontWeight: 800,
              color: isOverridden ? 'var(--warning)' : 'var(--brand-primary)',
              fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)',
            }}>
              {formatMoney(subtotal, currency)}
            </p>
          </div>
        )}
      </>
  );

  const confirm = (
        <div style={stickyConfirm}>
          <button
            className="btn btn-primary btn-lg"
            onClick={onConfirm}
            disabled={!qty}
            style={{ width: '100%' }}
          >
            {isEditing ? t('common.save') : t('cashier.qtySheet.addToCart')}
          </button>
        </div>
  );

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-sheet" style={landscape ? landscapeSheet : undefined}>
        {landscape ? (
          // Слева — товар, поля, сумма и кнопка; справа — клавиатура
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'stretch' }}>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              {header}
              {fields}
              {extras}
              <div style={{ flex: 1 }} />
              {confirm}
            </div>
            <div style={{ alignSelf: 'end' }}>{pad}</div>
          </div>
        ) : (
          <>
            {header}
            {fields}
            {pad}
            {extras}
            {confirm}
          </>
        )}
      </div>
    </div>
  );
}

// Карточка-кнопка для выбора активного поля
function FieldTile({ active, onClick, label, value, placeholder, suffix, edited }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? 'var(--brand-primary-soft)' : 'var(--bg-subtle)',
        border: '2px solid', borderColor: active ? 'var(--brand-primary)' : 'transparent',
        borderRadius: 'var(--r-sm)',
        padding: 12,
        cursor: 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
        minWidth: 0,
      }}
    >
      <p style={{
        fontSize: 10, fontWeight: 700,
        color: active ? 'var(--brand-primary)' : (edited ? 'var(--warning)' : 'var(--fg-muted)'),
        letterSpacing: '0.06em',
      }}>
        {label}
      </p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
        <span style={{
          fontSize: 24, fontWeight: 800,
          color: value ? 'var(--fg-strong)' : 'var(--fg-faint)',
          fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)',
          flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {value || placeholder}
        </span>
        <span style={{ fontSize: 12, color: 'var(--fg-muted)', flexShrink: 0 }}>{suffix}</span>
      </div>
    </button>
  );
}


// ─── DiscountSheet ─────────────────────────────────────────────
// Bottom sheet для применения скидки на товар или на чек целиком.
// target: 'sale' | { cartKey: string }
// onApply(value | null) — value: { type, value } или null чтобы убрать
function DiscountSheet({ target, cart, saleDiscount, subtotal, maxPercent, maxAmount, isOwner, currency, onApply, onClose, showError }) {
  const { t } = useTranslation();

  // Какой объект редактируем — текущая скидка для товара или для чека
  const targetItem = target !== 'sale' ? cart.find(i => (i._cartKey || i.id) === target.cartKey) : null;
  const baseAmount = target === 'sale'
    ? subtotal
    : (targetItem ? targetItem.salePrice * targetItem.qty : 0);
  const targetTitle = target === 'sale'
    ? t('discounts.discountOnSale')
    : (targetItem ? targetItem.name : t('discounts.discountOnItem'));

  const initial = target === 'sale'
    ? saleDiscount
    : (targetItem?.discountType ? { type: targetItem.discountType, value: targetItem.discountValue } : null);

  // Доступность типов скидки. Владелец может всегда. Сотрудник — только если лимит > 0.
  const canPercent = isOwner || maxPercent > 0;
  const canAmount  = isOwner || maxAmount > 0;

  // Если выбранный тип запрещён — переключаем на разрешённый
  const [type, setType] = useState(() => {
    const init = initial?.type || 'percent';
    if (init === 'percent' && !canPercent && canAmount) return 'amount';
    if (init === 'amount' && !canAmount && canPercent) return 'percent';
    return init;
  });
  const [value, setValue] = useState(initial?.value ? String(initial.value) : '');

  const numValue = parseFloat(String(value).replace(',', '.')) || 0;
  const previewDisc = (() => {
    if (numValue <= 0 || baseAmount <= 0) return 0;
    if (type === 'percent') return Math.min(baseAmount, (baseAmount * Math.min(numValue, 100)) / 100);
    return Math.min(numValue, baseAmount);
  })();
  const previewTotal = baseAmount - previewDisc;

  const apply = () => {
    if (numValue <= 0) {
      onApply(null);
      return;
    }
    if (!isOwner) {
      if (type === 'percent') {
        if (maxPercent === 0) return showError(t('discounts.percentNotAllowed'));
        if (numValue > maxPercent) {
          return showError(t('discounts.errorOverPercentLimit', { max: maxPercent }));
        }
      }
      if (type === 'amount') {
        if (maxAmount === 0) return showError(t('discounts.amountNotAllowed'));
        if (numValue > maxAmount) {
          return showError(t('discounts.errorOverAmountLimit', { max: maxAmount }));
        }
      }
    }
    onApply({ type, value: numValue });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 200,
        display: 'flex', alignItems: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          background: 'var(--bg)',
          borderRadius: '16px 16px 0 0',
          padding: 20,
          maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700 }}>{t('discounts.discountTitle')}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-muted)' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 4 }}>
          {targetTitle}
        </p>
        <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginBottom: 12, fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(baseAmount, currency)}
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[
            { code: 'percent', label: t('discounts.typePercent'), enabled: canPercent },
            { code: 'amount',  label: t('discounts.typeAmount'),  enabled: canAmount },
          ].map(tp => (
            <button
              key={tp.code}
              onClick={() => tp.enabled && setType(tp.code)}
              disabled={!tp.enabled}
              style={{
                flex: 1, padding: '10px',
                borderRadius: 'var(--r-sm)',
                border: '1px solid',
                borderColor: type === tp.code ? 'var(--brand-primary)' : 'var(--border)',
                background: type === tp.code ? 'var(--brand-primary-soft)' : 'var(--bg-elevated)',
                color: type === tp.code ? 'var(--brand-primary)' : 'var(--fg-muted)',
                fontWeight: 700,
                cursor: tp.enabled ? 'pointer' : 'not-allowed',
                opacity: tp.enabled ? 1 : 0.4,
              }}
            >
              {tp.label}
            </button>
          ))}
        </div>

        <div className="input-group">
          <label>{type === 'percent' ? t('discounts.percentLabel') : t('discounts.amountLabel')}</label>
          <input
            className="input"
            type="number"
            inputMode="decimal"
            min={0}
            step={type === 'percent' ? 1 : 0.01}
            value={value}
            onChange={e => setValue(e.target.value)}
            autoFocus
            style={{ fontSize: 18, fontWeight: 600, textAlign: 'center' }}
          />
          {!isOwner && (
            <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
              {type === 'percent' && maxPercent > 0 && maxPercent < 100 && t('discounts.maxPercent', { percent: maxPercent })}
              {type === 'amount' && maxAmount > 0 && t('discounts.maxAmount', { amount: formatMoney(maxAmount, currency) })}
            </p>
          )}
        </div>

        {numValue > 0 && (
          <div style={{
            padding: 10,
            background: 'var(--bg-subtle)',
            borderRadius: 'var(--r-sm)',
            marginBottom: 12,
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--fg-muted)' }}>
              <span>{t('discounts.discount')}</span>
              <span style={{ color: 'var(--brand-accent)', fontWeight: 600 }}>−{formatMoney(previewDisc, currency)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 4 }}>
              <span>{t('discounts.discountedTo')}</span>
              <span>{formatMoney(previewTotal, currency)}</span>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          {initial && (
            <button
              onClick={() => onApply(null)}
              className="btn btn-ghost"
              style={{ flex: 1, color: 'var(--danger)' }}
            >
              {t('discounts.remove')}
            </button>
          )}
          <button
            onClick={apply}
            className="btn btn-primary"
            style={{ flex: 1 }}
          >
            {t('discounts.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── VariantPickerSheet ─────────────────────────────────────
// Bottom sheet выбора варианта товара при тапе на товар с несколькими вариантами.
// Показывает каждый вариант: фото, имя (атрибуты), цена, остаток.
function VariantPickerSheet({ product, currency, onPick, onClose }) {
  const { t } = useTranslation();
  const variants = (product.variants || []).filter(v => v.active !== false);
  const productPhoto = product.photo
    || (Array.isArray(product.photos) && product.photos[0])
    || null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 200,
        display: 'flex', alignItems: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          background: 'var(--bg)',
          borderRadius: '16px 16px 0 0',
          padding: 20,
          maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {product.name}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-muted)' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 12 }}>
          {t('variants.pickVariant') || 'Выбери вариант'}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' }}>
          {variants.map(v => {
            const variantPhoto = (Array.isArray(v.photos) && v.photos[0]) || productPhoto;
            const attrLine = v.attributeValues
              ? Object.entries(v.attributeValues).map(([k, val]) => val).filter(Boolean).join(' / ')
              : '';
            const stock = Number(v.stock) || 0;
            const lowStock = stock <= 0;
            return (
              <button
                key={v.id}
                onClick={() => onPick(v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: 12,
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-sm)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  opacity: lowStock ? 0.55 : 1,
                }}
              >
                {variantPhoto ? (
                  <img
                    src={variantPhoto}
                    alt=""
                    style={{
                      width: 48, height: 48,
                      borderRadius: 'var(--r-xs)',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div style={{
                    width: 48, height: 48,
                    borderRadius: 'var(--r-xs)',
                    background: 'var(--bg-subtle)',
                    flexShrink: 0,
                  }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600 }}>
                    {attrLine || v.name || t('variants.defaultVariant')}
                  </p>
                  <p style={{ fontSize: 11, color: 'var(--fg-faint)', fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(v.salePrice || 0, currency)} · {t('products.stock')}: {stock}
                    {v.sku && <> · {v.sku}</>}
                  </p>
                </div>
                <Icon name="chevronRight" size={16} color="var(--fg-faint)" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Шит выбора должника для продажи в долг ───────────────────
function DebtorPickerSheet({ total, currency, processing, onConfirm, onClose }) {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [paidNow, setPaidNow] = useState('0');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newWhatsapp, setNewWhatsapp] = useState('');
  const [busy, setBusy] = useState(false);
  const [scanErr, setScanErr] = useState('');
  // Новый клиент по QR — ждём «Добавить?» от продавца (молча не создаём)
  const [pendingScan, setPendingScan] = useState(null); // { code, name, whatsapp }
  // Старый должник без WhatsApp — номер обязателен, просим вписать
  const [fixWa, setFixWa] = useState(null);             // debtor
  const [fixWaValue, setFixWaValue] = useState('');

  // Выбор должника из списка или по скану — с проверкой чёрного списка и номера
  const pick = (d) => {
    setScanErr('');
    if (d.creditBlocked) { setScanErr(t('debtors.blacklisted')); return; }
    if (!isValidWhatsapp(d.whatsapp || d.phone)) { setFixWa(d); setFixWaValue(''); return; }
    setSelected(d);
  };

  // Скан QR клиента: находит должника — не надо искать в списке.
  const handleDebtorScan = async (code, create = false) => {
    if (!code) return;
    setScanErr('');
    setBusy(true);
    try {
      const d = await debtorsApi.resolveScan(code, create);
      if (d.found === false) {
        setPendingScan({ code, ...d.customer });
      } else {
        setPendingScan(null);
        if (d.created) load(q.trim()); // новый должник — сразу виден в списке
        pick({ id: d.id, name: d.name, whatsapp: d.whatsapp, debt: d.debt || 0, creditBlocked: d.creditBlocked });
      }
    } catch (e) {
      const errCode = e?.data?.code;
      setScanErr(
        errCode === 'CUSTOMER_NO_WHATSAPP' ? t('debtors.customerNoWhatsapp')
        : (e?.status === 400 || e?.status === 404) ? t('debtors.scanNotFound', 'Клиент не найден. Попросите показать QR из приложения.')
        : errorMessage(e, t)
      );
    }
    setBusy(false);
  };

  const saveFixWa = async () => {
    if (!isValidWhatsapp(fixWaValue) || busy) return;
    setBusy(true);
    try {
      const upd = await debtorsApi.update(fixWa.id, { whatsapp: fixWaValue.trim() });
      const d = { ...fixWa, whatsapp: upd.whatsapp };
      setFixWa(null);
      setSelected(d);
    } catch (e) { setScanErr(errorMessage(e, t)); }
    setBusy(false);
  };

  const load = (query) => {
    setLoading(true);
    debtorsApi.list(query).then(r => setList(r.items || [])).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(() => {
    const id = setTimeout(() => load(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  const createDebtor = async () => {
    if (!newName.trim() || !isValidWhatsapp(newWhatsapp) || busy) return;
    setBusy(true);
    setScanErr('');
    try {
      const d = await debtorsApi.create({ name: newName.trim(), whatsapp: newWhatsapp.trim() });
      load(q.trim());
      setSelected(d);

      setAdding(false);
      setNewName(''); setNewWhatsapp('');
    } catch (e) { setScanErr(errorMessage(e, t)); }
    setBusy(false);
  };

  const paid = Math.min(Math.max(0, Number(paidNow) || 0), total);
  const credit = Math.round((total - paid) * 100) / 100;

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-sheet">
        {pendingScan && !selected ? (
          <>
            <h3 className="modal-title">{t('debtors.newFromScanTitle')}</h3>
            <div style={{ background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)', padding: 14, marginBottom: 12 }}>
              <p style={{ fontSize: 16, fontWeight: 700 }}>{pendingScan.name}</p>
              <p style={{ fontSize: 14, color: 'var(--fg-muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="whatsapp" size={14} /> {pendingScan.whatsapp}
              </p>
            </div>
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 12 }}>{t('debtors.newFromScanHint')}</p>
            {scanErr && <p style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 8, textAlign: 'center' }}>{scanErr}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setPendingScan(null)} style={{ flex: 1 }}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={() => handleDebtorScan(pendingScan.code, true)} disabled={busy} style={{ flex: 2 }}>
                {busy ? '…' : t('common.add')}
              </button>
            </div>
          </>
        ) : fixWa && !selected ? (
          <>
            <h3 className="modal-title">{fixWa.name}</h3>
            <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 10 }}>{t('debtors.needWhatsapp')}</p>
            <input className="input" type="tel" inputMode="tel" placeholder={t('debtors.whatsappRequired')} value={fixWaValue}
              onChange={(e) => setFixWaValue(e.target.value)} style={{ marginBottom: 10 }} autoFocus />
            {scanErr && <p style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 8, textAlign: 'center' }}>{scanErr}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setFixWa(null)} style={{ flex: 1 }}>{t('common.back')}</button>
              <button className="btn btn-primary" onClick={saveFixWa} disabled={!isValidWhatsapp(fixWaValue) || busy} style={{ flex: 2 }}>
                {busy ? '…' : t('common.save')}
              </button>
            </div>
          </>
        ) : !selected ? (
          <>
            <h3 className="modal-title">{t('debtors.pickTitle')}</h3>
            {!adding ? (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <input className="input" placeholder={t('debtors.searchPlaceholder')} value={q}
                    onChange={(e) => setQ(e.target.value)} style={{ flex: 1, marginBottom: 0 }} />
                  <ScanButton raw disabled={busy} onScan={handleDebtorScan} size="md" />
                </div>
                {scanErr && (
                  <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--danger)', background: 'var(--danger-soft, #fde8e8)', border: '1px solid var(--danger)', borderRadius: 'var(--r-sm)', padding: '10px 12px', marginBottom: 8, textAlign: 'center' }}>{scanErr}</p>
                )}
                <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginBottom: 10, textAlign: 'center' }}>
                  {t('debtors.scanHint', 'Или отсканируйте QR клиента из его приложения')}
                </p>
                <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {loading && <div className="loader" style={{ padding: 16 }}><div className="spinner" /></div>}
                  {!loading && list.length === 0 && (
                    <p style={{ fontSize: 13, color: 'var(--fg-faint)', textAlign: 'center', padding: 12 }}>{t('debtors.emptyShort')}</p>
                  )}
                  {list.map(d => (
                    <button key={d.id} onClick={() => pick(d)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', cursor: 'pointer', textAlign: 'left', opacity: d.creditBlocked ? 0.55 : 1 }}>
                      <span style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {d.name}
                        {d.creditBlocked && <span style={{ fontSize: 11, color: 'var(--danger)', border: '1px solid var(--danger)', borderRadius: 4, padding: '1px 5px' }}>{t('debtors.blacklistTag')}</span>}
                      </span>
                      {d.debt > 0 && (
                        <span style={{ fontSize: 12, color: 'var(--danger)', fontVariantNumeric: 'tabular-nums' }}>
                          {t('debtors.owesShort')}: {formatMoney(d.debt, currency)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <button className="btn btn-secondary" onClick={() => setAdding(true)} style={{ width: '100%', marginTop: 10 }}>
                  <Icon name="users" size={15} /> {t('debtors.addNew')}
                </button>
              </>
            ) : (
              <>
                <input className="input" placeholder={t('debtors.name')} value={newName}
                  onChange={(e) => setNewName(e.target.value)} style={{ marginBottom: 8 }} autoFocus />
                <input className="input" type="tel" inputMode="tel" placeholder={t('debtors.whatsappRequired')} value={newWhatsapp}
                  onChange={(e) => setNewWhatsapp(e.target.value)} style={{ marginBottom: 10 }} />
                {scanErr && <p style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 8, textAlign: 'center' }}>{scanErr}</p>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-ghost" onClick={() => setAdding(false)} style={{ flex: 1 }}>{t('common.back')}</button>
                  <button className="btn btn-primary" onClick={createDebtor} disabled={!newName.trim() || !isValidWhatsapp(newWhatsapp) || busy} style={{ flex: 1 }}>{busy ? '…' : t('common.add')}</button>

                </div>
              </>
            )}
          </>
        ) : (
          <>
            <h3 className="modal-title">{selected.name}</h3>
            {selected.debt > 0 && (
              <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: -6, marginBottom: 10 }}>
                {t('debtors.currentDebt')}: <b style={{ color: 'var(--danger)' }}>{formatMoney(selected.debt, currency)}</b>
              </p>
            )}
            <div style={{ background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)', padding: 14, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
                <span>{t('debtors.checkTotal')}</span>
                <b style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(total, currency)}</b>
              </div>
              <label style={{ fontSize: 12, color: 'var(--fg-muted)', display: 'block', marginBottom: 4 }}>{t('debtors.paidNow')}</label>
              <input className="input" type="number" inputMode="decimal" value={paidNow}
                onChange={(e) => setPaidNow(e.target.value)} style={{ marginBottom: 8 }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: credit > 0 ? 'var(--danger)' : 'var(--success)' }}>
                <span>{t('debtors.toDebt')}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(credit, currency)}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setSelected(null)} style={{ flex: 1 }}>{t('common.back')}</button>
              <button className="btn btn-primary" onClick={() => onConfirm(selected.id, paid)} disabled={processing} style={{ flex: 2 }}>
                {processing ? t('cashier.processing') : t('debtors.confirmCredit')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
